import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Plane, 
  Clock, 
  Filter, 
  ChevronDown, 
  ChevronUp,
  Sun,
  Sunset,
  Moon,
  Edit3,
  ArrowRight,
  Check,
  PlaneTakeoff,
  PlaneLanding,
  Calendar as CalendarIcon,
  CalendarDays,
  Armchair,
  Briefcase,
  Wifi,
  Tv,
  UtensilsCrossed,
  RefreshCw,
  Globe,
  Star,
  Timer,
  X,
  Search,
  Info
} from 'lucide-react';
import { format } from 'date-fns';
import { AirlineLogo } from '../utils/airlineLogos';
import { MobileFilterButton } from './MobileFilterButton';
import { BfmFlightCard } from './flight/BfmFlightCard';
import { FlightSearch } from './FlightSearch';
import { Money } from '../contexts/CurrencyContext';

// Airline Alliance Data
const AIRLINE_ALLIANCES = {
  'Star Alliance': ['LH', 'UA', 'AC', 'NH', 'SQ', 'TK', 'SK', 'OS', 'LO', 'TP', 'MS', 'ET', 'AI', 'CA', 'AV', 'BR', 'OZ', 'SA', 'SN', 'TG', 'EI'],
  'OneWorld': ['BA', 'AA', 'QF', 'CX', 'IB', 'JL', 'MH', 'QR', 'RJ', 'AT', 'AY', 'S7', 'UL', 'FJ'],
  'SkyTeam': ['AF', 'KL', 'DL', 'AM', 'KE', 'VN', 'CI', 'MU', 'CZ', 'SU', 'GA', 'ME', 'SV', 'RO', 'OK']
};

// Wide-body aircraft codes
const WIDEBODY_AIRCRAFT = ['777', '787', '747', '767', '330', '340', '350', '380', 'A330', 'A340', 'A350', 'A380', 'B777', 'B787', 'B747', 'B767'];

export const FlightResults = ({ 
  flights, 
  pinnedFlights = [],  // Preferred airline direct flights
  pinnedAirlineName = null,  // e.g., "Emirates", "Qatar Airways"
  pinnedAirlineCode = null,  // e.g., "EK", "QR"
  isFlexible, 
  onSelectFlight, 
  searchParams,
  isLoading,
  isRevalidating,
  revalidationError,
  onModifySearch,
  onShowFareGrid,
  onInlineSearch,
  onClearRevalidationError
}) => {
  // Inline search panel state
  const [showInlineSearch, setShowInlineSearch] = useState(false);
  
  // Price Display mode - 'perPerson' or 'total'
  const [priceDisplayMode, setPriceDisplayMode] = useState('perPerson');
  
  // Airline brand colors - comprehensive mapping for all major airlines
  const AIRLINE_COLORS = {
    // Middle East & Gulf
    'PK': { bg: 'bg-green-700', header: 'bg-gradient-to-r from-green-700 to-green-600', border: 'border-green-200', borderHover: 'hover:border-green-400', text: 'text-green-600', badge: 'bg-green-700' },
    'EK': { bg: 'bg-red-600', header: 'bg-gradient-to-r from-red-600 to-red-500', border: 'border-red-200', borderHover: 'hover:border-red-400', text: 'text-red-600', badge: 'bg-red-600' },
    'SV': { bg: 'bg-green-600', header: 'bg-gradient-to-r from-green-700 to-green-500', border: 'border-green-200', borderHover: 'hover:border-green-400', text: 'text-green-600', badge: 'bg-green-600' },
    'QR': { bg: 'bg-purple-800', header: 'bg-gradient-to-r from-purple-900 to-purple-700', border: 'border-purple-200', borderHover: 'hover:border-purple-400', text: 'text-purple-600', badge: 'bg-purple-800' },
    'EY': { bg: 'bg-amber-600', header: 'bg-gradient-to-r from-amber-700 to-amber-500', border: 'border-amber-200', borderHover: 'hover:border-amber-400', text: 'text-amber-600', badge: 'bg-amber-600' },
    'GF': { bg: 'bg-red-700', header: 'bg-gradient-to-r from-red-800 to-red-600', border: 'border-red-200', borderHover: 'hover:border-red-400', text: 'text-red-600', badge: 'bg-red-700' },
    'WY': { bg: 'bg-teal-600', header: 'bg-gradient-to-r from-teal-700 to-teal-500', border: 'border-teal-200', borderHover: 'hover:border-teal-400', text: 'text-teal-600', badge: 'bg-teal-600' },
    'RJ': { bg: 'bg-blue-800', header: 'bg-gradient-to-r from-blue-900 to-blue-700', border: 'border-blue-200', borderHover: 'hover:border-blue-400', text: 'text-blue-600', badge: 'bg-blue-800' },
    'MS': { bg: 'bg-blue-600', header: 'bg-gradient-to-r from-blue-700 to-blue-500', border: 'border-blue-200', borderHover: 'hover:border-blue-400', text: 'text-blue-600', badge: 'bg-blue-600' },
    // European
    'BA': { bg: 'bg-blue-900', header: 'bg-gradient-to-r from-blue-900 to-red-700', border: 'border-blue-200', borderHover: 'hover:border-blue-400', text: 'text-blue-600', badge: 'bg-blue-900' },
    'AF': { bg: 'bg-blue-700', header: 'bg-gradient-to-r from-blue-800 to-blue-600', border: 'border-blue-200', borderHover: 'hover:border-blue-400', text: 'text-blue-600', badge: 'bg-blue-700' },
    'KL': { bg: 'bg-sky-600', header: 'bg-gradient-to-r from-sky-700 to-sky-500', border: 'border-sky-200', borderHover: 'hover:border-sky-400', text: 'text-sky-600', badge: 'bg-sky-600' },
    'LH': { bg: 'bg-yellow-600', header: 'bg-gradient-to-r from-blue-900 to-yellow-500', border: 'border-yellow-200', borderHover: 'hover:border-yellow-400', text: 'text-yellow-600', badge: 'bg-yellow-600' },
    'TK': { bg: 'bg-red-600', header: 'bg-gradient-to-r from-red-700 to-red-500', border: 'border-red-200', borderHover: 'hover:border-red-400', text: 'text-red-600', badge: 'bg-red-600' },
    'VS': { bg: 'bg-red-600', header: 'bg-gradient-to-r from-red-700 to-purple-600', border: 'border-red-200', borderHover: 'hover:border-red-400', text: 'text-red-600', badge: 'bg-red-600' },
    'LX': { bg: 'bg-red-600', header: 'bg-gradient-to-r from-red-700 to-red-500', border: 'border-red-200', borderHover: 'hover:border-red-400', text: 'text-red-600', badge: 'bg-red-600' },
    // Low Cost
    'U2': { bg: 'bg-orange-500', header: 'bg-gradient-to-r from-orange-600 to-orange-400', border: 'border-orange-200', borderHover: 'hover:border-orange-400', text: 'text-orange-600', badge: 'bg-orange-500' },
    'FR': { bg: 'bg-blue-800', header: 'bg-gradient-to-r from-blue-900 to-yellow-500', border: 'border-blue-200', borderHover: 'hover:border-blue-400', text: 'text-blue-600', badge: 'bg-blue-800' },
    'W6': { bg: 'bg-pink-600', header: 'bg-gradient-to-r from-pink-700 to-pink-500', border: 'border-pink-200', borderHover: 'hover:border-pink-400', text: 'text-pink-600', badge: 'bg-pink-600' },
    // US
    'AA': { bg: 'bg-red-600', header: 'bg-gradient-to-r from-red-700 to-blue-700', border: 'border-red-200', borderHover: 'hover:border-red-400', text: 'text-red-600', badge: 'bg-red-600' },
    'UA': { bg: 'bg-blue-700', header: 'bg-gradient-to-r from-blue-800 to-blue-600', border: 'border-blue-200', borderHover: 'hover:border-blue-400', text: 'text-blue-600', badge: 'bg-blue-700' },
    'DL': { bg: 'bg-blue-800', header: 'bg-gradient-to-r from-blue-900 to-red-600', border: 'border-blue-200', borderHover: 'hover:border-blue-400', text: 'text-blue-600', badge: 'bg-blue-800' },
    // Asian
    'SQ': { bg: 'bg-yellow-600', header: 'bg-gradient-to-r from-blue-900 to-yellow-500', border: 'border-yellow-200', borderHover: 'hover:border-yellow-400', text: 'text-yellow-600', badge: 'bg-yellow-600' },
    'CX': { bg: 'bg-green-600', header: 'bg-gradient-to-r from-green-800 to-green-600', border: 'border-green-200', borderHover: 'hover:border-green-400', text: 'text-green-600', badge: 'bg-green-600' },
    'AI': { bg: 'bg-orange-600', header: 'bg-gradient-to-r from-orange-700 to-red-500', border: 'border-orange-200', borderHover: 'hover:border-orange-400', text: 'text-orange-600', badge: 'bg-orange-600' },
    // African
    'ET': { bg: 'bg-green-600', header: 'bg-gradient-to-r from-green-700 to-yellow-500', border: 'border-green-200', borderHover: 'hover:border-green-400', text: 'text-green-600', badge: 'bg-green-600' },
  };
  
  // Get airline colors dynamically - returns default blue if airline not mapped
  const getAirlineColors = (airlineCode) => {
    return AIRLINE_COLORS[airlineCode] || { 
      bg: 'bg-blue-600', 
      header: 'bg-gradient-to-r from-blue-700 to-blue-500', 
      border: 'border-blue-200', 
      borderHover: 'hover:border-blue-400', 
      text: 'text-blue-600', 
      badge: 'bg-blue-600' 
    };
  };
  
  // Get colors for the pinned airline (default to red if unknown)
  const pinnedColors = AIRLINE_COLORS[pinnedAirlineCode] || { bg: 'bg-red-600', header: 'bg-red-600', border: 'border-red-200', borderHover: 'hover:border-red-400', text: 'text-red-600', badge: 'bg-red-600' };
  
  // Filter states
  const [filters, setFilters] = useState({
    fareType: null,
    stopsOutbound: null,
    stopsReturn: null,
    baggage: null,
    airlines: [],
    outboundDepartureTime: null,
    outboundArrivalTime: null,
    returnDepartureTime: null,
    returnArrivalTime: null,
    connectionLengthOutbound: null,
    connectionLengthReturn: null,
    departureAirport: [],
    arrivalAirport: [],
    connectingIn: [],
    minPrice: '',
    maxPrice: '',
    // New filters
    flightDuration: null,
    overnightFlights: null,
    refundable: null,
    flexible: null,
    alliance: [],
    preferredAirlines: [],
    cabinClass: null,
    wifiAvailable: null,
    entertainmentAvailable: null,
    mealIncluded: null,
    aircraftType: null
  });
  const [sortBy, setSortBy] = useState('price'); // Default to cheapest first
  const [selectedOutbound, setSelectedOutbound] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [viewMode, setViewMode] = useState('combined'); // 'combined' or 'separate'
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const flightsPerPage = 30; // 30 flights per page for 10+ pages with 300+ flights
  
  // Selected flexible date filter (null = show all, 0 = base date, -3 to 3 = offset)
  const [selectedDateOffset, setSelectedDateOffset] = useState(null);
  
  // Full calendar modal state (Skyscanner-style)
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarDepartDate, setCalendarDepartDate] = useState(null);
  const [calendarReturnDate, setCalendarReturnDate] = useState(null);
  
  // Fare calendar prices for flexible dates bar
  const [fareCalendarPrices, setFareCalendarPrices] = useState({});
  const [fareCalendarLoading, setFareCalendarLoading] = useState(false);
  
  // Generate estimated prices for nearby dates based on actual search results
  // Extended to cover 12 months like Skyscanner
  useEffect(() => {
    if (!flights || flights.length === 0 || !searchParams?.departure_date) {
      setFareCalendarPrices({});
      return;
    }
    
    // Get the base price from actual search results (lowest price)
    const basePrice = Math.min(...flights.map(f => f.price || Infinity));
    const baseDateStr = searchParams.departure_date;
    
    // Generate estimated prices for 12 months ahead
    const estimates = {};
    const baseDate = new Date(baseDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate prices for 365 days ahead from today
    for (let i = 0; i <= 365; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Calculate days from base search date
      const daysDiff = Math.floor((date - baseDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 0) {
        // Use actual lowest price for searched date
        estimates[dateStr] = basePrice;
      } else {
        // Estimate prices with realistic variance
        // Weekends tend to be more expensive
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        const dayFactor = isWeekend ? 1.12 : 1.0; // 12% higher on weekends
        
        // Seasonal pricing - summer (Jun-Aug) and holidays more expensive
        const month = date.getMonth();
        let seasonFactor = 1.0;
        if (month >= 5 && month <= 7) seasonFactor = 1.15; // Summer 15% higher
        if (month === 11 || month === 0) seasonFactor = 1.20; // Dec/Jan 20% higher (holidays)
        if (month === 3 || month === 4) seasonFactor = 1.10; // Spring 10% higher
        
        // Distance from base date affects variance
        const distanceFactor = 1 + (Math.abs(daysDiff) * 0.003); // ~0.3% per day
        
        // Add consistent pseudo-random variance based on date
        const dateHash = (date.getDate() * 7 + date.getMonth() * 13 + date.getFullYear()) % 100;
        const randomFactor = 0.90 + (dateHash / 100) * 0.25; // 90% to 115%
        
        const estimatedPrice = Math.round(basePrice * dayFactor * seasonFactor * distanceFactor * randomFactor);
        estimates[dateStr] = estimatedPrice;
      }
    }
    
    setFareCalendarPrices(estimates);
  }, [flights, searchParams?.departure_date]);
  
  // Handle inline search submission
  const handleInlineSearchSubmit = (searchData) => {
    setShowInlineSearch(false);
    if (onInlineSearch) {
      onInlineSearch(searchData);
    } else if (onModifySearch) {
      // Fallback to original behavior
      onModifySearch();
    }
  };
  
  // Initialize filters based on searchParams (especially direct_flights)
  useEffect(() => {
    if (searchParams?.direct_flights || searchParams?.directFlights) {
      setFilters(prev => ({
        ...prev,
        stopsOutbound: 'direct',
        stopsReturn: 'direct'
      }));
    }
  }, [searchParams]);
  
  // Reset to page 1 when filters or date offset change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy, selectedDateOffset]);
  
  // Scroll to top when flights load to show cheapest first
  useEffect(() => {
    if (flights && flights.length > 0) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [flights]);
  
  // Helper to scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Helper to scroll to flight results section (used when filters change)
  // Scrolls to TOP of page to show "X Flights Available" header and first matching flights
  const scrollToFlightResults = () => {
    // Scroll to absolute top of page so flights appear at the top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Wrapper for selecting outbound flight
  const handleSelectOutbound = (flight) => {
    setSelectedOutbound(flight);
    scrollToTop();
  };
  
  // Wrapper for selecting return flight
  const handleSelectReturn = (flight) => {
    setSelectedReturn(flight);
    scrollToTop();
  };
  
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    fareType: false,
    stopsOutbound: true,
    stopsReturn: true,
    baggage: true,
    airlines: true,
    outboundDepartureTime: true,
    outboundArrivalTime: true,
    returnDepartureTime: true,
    returnArrivalTime: true,
    connectionLengthOutbound: true,
    connectionLengthReturn: true,
    departureAirport: true,
    arrivalAirport: true,
    connectingIn: true,
    price: true,
    // New filter sections
    flightDuration: true,
    overnightFlights: true,
    refundable: true,
    flexible: true,
    alliance: true,
    preferredAirlines: false,
    cabinClass: true,
    amenities: true,
    aircraftType: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ==========================================
  // STATE PERSISTENCE FOR BROWSER REFRESH
  // ==========================================
  
  // Save flight results UI state to sessionStorage
  useEffect(() => {
    const uiState = {
      filters,
      sortBy,
      viewMode,
      priceDisplayMode,
      expandedSections,
      currentPage,
      selectedOutbound: selectedOutbound?.id || null,
      selectedReturn: selectedReturn?.id || null
    };
    sessionStorage.setItem('flightResultsUIState', JSON.stringify(uiState));
  }, [filters, sortBy, viewMode, priceDisplayMode, expandedSections, currentPage, selectedOutbound, selectedReturn]);
  
  // Restore flight results UI state on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('flightResultsUIState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        console.log('Restoring flight results UI state');
        if (state.filters) setFilters(state.filters);
        if (state.sortBy) setSortBy(state.sortBy);
        if (state.viewMode) setViewMode(state.viewMode);
        if (state.priceDisplayMode) setPriceDisplayMode(state.priceDisplayMode);
        if (state.expandedSections) setExpandedSections(state.expandedSections);
        if (state.currentPage) setCurrentPage(state.currentPage);
        // Restore selected flights if IDs match current flights
        if (state.selectedOutbound && flights.length > 0) {
          const found = flights.find(f => f.id === state.selectedOutbound);
          if (found) setSelectedOutbound(found);
        }
        if (state.selectedReturn && flights.length > 0) {
          const found = flights.find(f => f.id === state.selectedReturn);
          if (found) setSelectedReturn(found);
        }
      } catch (e) {
        console.error('Failed to restore flight results UI state:', e);
      }
    }
  }, []); // Only run on mount

  // Check if this is a round-trip search
  const isRoundTrip = searchParams?.return_date && flights.some(f => f.return_departure_time);
  
  // Calculate cheapest price and fastest duration for sorting buttons
  const cheapestPrice = useMemo(() => {
    if (flights.length === 0) return null;
    return Math.round(Math.min(...flights.map(f => f.price || Infinity)));
  }, [flights]);

  const fastestDuration = useMemo(() => {
    if (flights.length === 0) return null;
    // Parse duration strings to find the shortest
    const parseDuration = (duration) => {
      if (!duration) return Infinity;
      const match = duration.match(/(\d+)h\s*(\d+)?m?/i);
      if (match) {
        const hours = parseInt(match[1]) || 0;
        const minutes = parseInt(match[2]) || 0;
        return hours * 60 + minutes;
      }
      return Infinity;
    };
    
    const shortestMinutes = Math.min(...flights.map(f => parseDuration(f.duration)));
    if (shortestMinutes === Infinity) return null;
    
    const hours = Math.floor(shortestMinutes / 60);
    const mins = shortestMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }, [flights]);

  // Extract unique values for filters
  const uniqueAirlines = useMemo(() => {
    const airlineMap = new Map();
    flights.forEach(f => {
      if (f.airline && !airlineMap.has(f.airline)) {
        const minPrice = flights
          .filter(flight => flight.airline === f.airline)
          .reduce((min, flight) => Math.min(min, flight.price), Infinity);
        airlineMap.set(f.airline, { name: f.airline, code: f.airline_code, minPrice });
      }
    });
    return Array.from(airlineMap.values()).sort((a, b) => a.minPrice - b.minPrice);
  }, [flights]);

  // Stop counts with prices
  const stopOptions = useMemo(() => {
    const direct = flights.filter(f => f.is_direct || f.stops === 0);
    const oneStop = flights.filter(f => f.stops === 1);
    const multiStop = flights.filter(f => f.stops >= 2);
    
    return [
      { 
        value: 'direct', 
        label: 'Direct', 
        count: direct.length,
        minPrice: direct.length > 0 ? Math.min(...direct.map(f => f.price)) : null
      },
      { 
        value: '1', 
        label: '1 Connection', 
        count: oneStop.length,
        minPrice: oneStop.length > 0 ? Math.min(...oneStop.map(f => f.price)) : null
      },
      { 
        value: '2+', 
        label: '2+ Connections', 
        count: multiStop.length,
        minPrice: multiStop.length > 0 ? Math.min(...multiStop.map(f => f.price)) : null
      }
    ];
  }, [flights]);

  // Time filter options
  const timeOptions = [
    { value: 'morning', label: 'Morning', time: '05:00 - 11:59', icon: Sun },
    { value: 'afternoon', label: 'Afternoon', time: '12:00 - 17:59', icon: Sun },
    { value: 'evening', label: 'Evening', time: '18:00 - 23:59', icon: Sunset },
    { value: 'overnight', label: 'Overnight', time: '00:00 - 04:59', icon: Moon }
  ];

  // Connection length filter options
  const connectionLengthOptions = [
    { value: 'none', label: 'No Connections', time: 'Direct only', minMinutes: 0, maxMinutes: 0 },
    { value: 'short', label: 'Short Connection', time: '0 - 2 hours', minMinutes: 1, maxMinutes: 120 },
    { value: 'relaxed', label: 'Relaxed Connection', time: '2 - 4 hours', minMinutes: 121, maxMinutes: 240 },
    { value: 'long', label: 'Long Connection', time: '4 - 8 hours', minMinutes: 241, maxMinutes: 480 },
    { value: 'verylong', label: 'Very Long Connection', time: '8+ hours', minMinutes: 481, maxMinutes: Infinity }
  ];

  // Fare type options
  const fareTypeOptions = [
    { value: 'Corp', label: 'Corp' },
    { value: 'IT', label: 'IT' },
    { value: 'Net', label: 'Net' },
    { value: 'Pub', label: 'Pub' },
    { value: 'VFR', label: 'VFR' }
  ];

  // Baggage options - simplified
  // Baggage filter options - structured with hold luggage filters
  const baggageOptions = [
    // 0 x Hold Luggage - no checked baggage (cabin bag only)
    { id: 'HOLD_0X', value: 'handonly', label: '0 × Hold Luggage', code: 'NO_HOLD', pieces: 0 },
    // 1 x Hold Luggage
    { 
      id: 'HOLD_1X', 
      value: '1piece',
      label: '1 × Hold Luggage', 
      code: 'CHECKED_1PC',
      pieces: 1,
      weightsKg: [23, 25, 30, 32, 35, 40]
    },
    // 2 x Hold Luggage
    { id: 'HOLD_2X', value: '2piece', label: '2 × Hold Luggage (2x23 = 46 kg)', code: 'CHECKED_2PC', pieces: 2, weightKg: 23 }
  ];

  // NEW FILTER OPTIONS
  
  // Flight Duration options
  const flightDurationOptions = [
    { value: 'short', label: 'Under 6 hours', maxMinutes: 360 },
    { value: 'medium', label: '6 - 12 hours', minMinutes: 360, maxMinutes: 720 },
    { value: 'long', label: '12+ hours', minMinutes: 720 }
  ];

  // Overnight flight options
  const overnightOptions = [
    { value: 'include', label: 'Include overnight flights' },
    { value: 'exclude', label: 'Exclude overnight flights' },
    { value: 'only', label: 'Only overnight flights' }
  ];

  // Refundable options
  const refundableOptions = [
    { value: 'refundable', label: 'Refundable tickets only' },
    { value: 'non-refundable', label: 'Non-refundable tickets' },
    { value: 'all', label: 'All tickets' }
  ];

  // Flexible tickets options
  const flexibleOptions = [
    { value: 'flexible', label: 'Flexible/Changeable only' },
    { value: 'fixed', label: 'Fixed tickets' },
    { value: 'all', label: 'All tickets' }
  ];

  // Cabin class options
  const cabinClassOptions = [
    { value: 'economy', label: 'Economy' },
    { value: 'premium', label: 'Premium Economy' },
    { value: 'business', label: 'Business Class' },
    { value: 'first', label: 'First Class' }
  ];

  // Aircraft type options
  const aircraftTypeOptions = [
    { value: 'widebody', label: 'Wide-body only (A350, 787, 777, etc.)' },
    { value: 'narrowbody', label: 'Narrow-body only (A320, 737, etc.)' },
    { value: 'all', label: 'All aircraft types' }
  ];

  // Get unique departure airports from flights
  // Define all London airports that should always be shown when LON is searched
  const LONDON_AIRPORTS = ['LCY', 'LGW', 'LHR', 'LTN', 'STN'];
  
  const uniqueDepartureAirports = useMemo(() => {
    const airports = new Set();
    flights.forEach(f => {
      if (f.from) airports.add(f.from);
    });
    
    // If searching from LON (Any London), always include all London airports
    const isLondonSearch = searchParams?.origin === 'LON' || 
      LONDON_AIRPORTS.some(apt => airports.has(apt));
    
    if (isLondonSearch) {
      // Add all London airports even if they don't have flights on this date
      LONDON_AIRPORTS.forEach(apt => airports.add(apt));
    }
    
    return Array.from(airports).sort();
  }, [flights, searchParams?.origin]);

  // Get unique fare types from flights
  const uniqueFareTypes = useMemo(() => {
    const fareTypes = new Set();
    flights.forEach(f => {
      // Check fare_attributes.fare_type or fare_details.fare_type
      const fareType = f.fare_attributes?.fare_type || f.fare_details?.fare_type || f.fare_type;
      if (fareType) fareTypes.add(fareType);
    });
    return Array.from(fareTypes).sort();
  }, [flights]);

  // Get unique arrival airports from flights
  const uniqueArrivalAirports = useMemo(() => {
    const airports = new Set();
    flights.forEach(f => {
      if (f.to) airports.add(f.to);
    });
    return Array.from(airports).sort();
  }, [flights]);

  // Get available alliances based on airlines in results
  const availableAlliances = useMemo(() => {
    const alliances = [];
    const airlineCodes = new Set(flights.map(f => f.airline_code || f.airline));
    
    Object.entries(AIRLINE_ALLIANCES).forEach(([alliance, codes]) => {
      const matchingAirlines = codes.filter(code => airlineCodes.has(code));
      if (matchingAirlines.length > 0) {
        alliances.push({
          name: alliance,
          count: flights.filter(f => codes.includes(f.airline_code || f.airline)).length
        });
      }
    });
    return alliances;
  }, [flights]);

  // Get unique connecting airports from layovers
  const uniqueConnectingAirports = useMemo(() => {
    const airports = new Map();
    flights.forEach(f => {
      if (f.layovers) {
        f.layovers.forEach(layover => {
          if (layover.airport && !airports.has(layover.airport)) {
            airports.set(layover.airport, layover.airport);
          }
        });
      }
      if (f.return_layovers) {
        f.return_layovers.forEach(layover => {
          if (layover.airport && !airports.has(layover.airport)) {
            airports.set(layover.airport, layover.airport);
          }
        });
      }
    });
    return Array.from(airports.keys()).sort();
  }, [flights]);

  // Calculate baggage filter counts
  const baggageCounts = useMemo(() => {
    let count0 = 0;  // 0 pieces (hand baggage only)
    let count1 = 0;  // 1 piece
    let count2 = 0;  // 2+ pieces
    
    flights.forEach(f => {
      const baggageCount = f.baggage_count ?? 0;
      const baggageIncluded = f.baggage_included !== false && baggageCount > 0;
      
      if (!baggageIncluded || baggageCount === 0) {
        count0++;
      } else if (baggageCount === 1) {
        count1++;
      } else if (baggageCount >= 2) {
        count2++;
      }
    });
    
    return { count0, count1, count2 };
  }, [flights]);

  // Parse the LOCAL-AIRPORT hour from a Sabre datetime string.
  //
  // Sabre returns times in the LOCAL TIME OF THE AIRPORT (e.g. JED departure
  // is reported in Saudi Arabia time). If we feed the raw string into
  // `new Date(...)` it gets converted to the user's BROWSER TIME — which
  // breaks for any cross-timezone flight (a LHR→JED flight would show
  // different hours to the user vs what Sabre Red shows).
  //
  // We therefore parse the hour DIRECTLY from the string, ignoring any
  // timezone offset.
  const getHour = (dateTimeOrTime) => {
    if (!dateTimeOrTime) return 12;
    const s = String(dateTimeOrTime);

    // HH:MM at the start (legacy InstaFlights format)
    const hhmm = s.match(/^(\d{2}):(\d{2})/);
    if (hhmm) return parseInt(hhmm[1], 10);

    // Full ISO datetime: YYYY-MM-DDTHH:MM[:SS][±HH:MM | Z]
    const iso = s.match(/T(\d{2}):(\d{2})/);
    if (iso) return parseInt(iso[1], 10);

    return 12;
  };

  const parseDuration = (duration) => {
    if (!duration) return 0;
    
    // Handle ISO format: PT5H30M
    const isoMatch = duration.match(/PT(\d+)H(\d+)?M?/);
    if (isoMatch) {
      const hours = parseInt(isoMatch[1]) || 0;
      const minutes = parseInt(isoMatch[2]) || 0;
      return hours * 60 + minutes;
    }
    
    // Handle simple format: "5h 30m" or "5h30m" or "5h"
    const simpleMatch = duration.match(/(\d+)h\s*(\d+)?m?/i);
    if (simpleMatch) {
      const hours = parseInt(simpleMatch[1]) || 0;
      const minutes = parseInt(simpleMatch[2]) || 0;
      return hours * 60 + minutes;
    }
    
    // Handle minutes only: "330m" or "330 minutes"
    const minsMatch = duration.match(/^(\d+)\s*m/i);
    if (minsMatch) {
      return parseInt(minsMatch[1]) || 0;
    }
    
    return 0;
  };

  // ==========================================
  // DYNAMIC SORTING CALCULATIONS
  // Build "Best / Cheapest / Fastest" from real search results
  // Using normalized scoring from current results
  // ==========================================
  
  // Map offer to standard format
  const mapOffer = (o) => {
    return {
      raw: o,
      price: Number(o.price ?? o.totalPrice ?? o.total_amount ?? 0),
      durationMins: Number(parseDuration(o.duration) || o.duration_mins || o.totalDurationMins || 0),
      stops: Number(o.stops ?? o.stopCount ?? 0),
      currency: o.currency ?? "GBP"
    };
  };

  // Normalize value between 0-1
  const norm = (x, min, max) => {
    if (!Number.isFinite(x) || !Number.isFinite(min) || !Number.isFinite(max) || max === min) return 0;
    return (x - min) / (max - min);
  };

  // Format duration helper
  const formatDurationMinutes = (mins) => {
    if (!Number.isFinite(mins) || mins <= 0) return "-";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${String(m).padStart(2, "0")}m`;
  };

  const sortingStats = useMemo(() => {
    if (!flights || flights.length === 0) {
      return {
        best: { price: '-', duration: '-' },
        cheapest: { price: '-', duration: '-' },
        fastest: { price: '-', duration: '-' }
      };
    }

    // Map and filter valid offers
    const offers = flights.map(mapOffer).filter(o => 
      Number.isFinite(o.price) && o.price > 0
    );

    if (!offers.length) {
      return {
        best: { price: '-', duration: '-' },
        cheapest: { price: '-', duration: '-' },
        fastest: { price: '-', duration: '-' }
      };
    }

    // Find Cheapest (lowest price)
    const cheapest = offers.reduce((a, b) => (b.price < a.price ? b : a));

    // Find Fastest (shortest duration)
    const offersWithDuration = offers.filter(o => o.durationMins > 0);
    const fastest = offersWithDuration.length > 0
      ? offersWithDuration.reduce((a, b) => (b.durationMins < a.durationMins ? b : a))
      : cheapest;

    // Find Best (normalized score from CURRENT results)
    const prices = offers.map(o => o.price);
    const durs = offers.map(o => o.durationMins).filter(d => d > 0);
    const maxStops = Math.max(...offers.map(o => o.stops), 0);

    const minP = Math.min(...prices), maxP = Math.max(...prices);
    const minD = durs.length ? Math.min(...durs) : 0;
    const maxD = durs.length ? Math.max(...durs) : 1;

    // Score function: weights = 55% price, 35% duration, 10% stops
    const score = (o) => {
      const p = norm(o.price, minP, maxP);
      const d = norm(o.durationMins || minD, minD, maxD);
      const s = maxStops ? (o.stops / maxStops) : 0;
      return 0.55 * p + 0.35 * d + 0.10 * s;
    };

    const best = offers.reduce((a, b) => (score(b) < score(a) ? b : a));

    return {
      best: { 
        price: Math.round(best.price), 
        duration: formatDurationMinutes(best.durationMins)
      },
      cheapest: { 
        price: Math.round(cheapest.price), 
        duration: formatDurationMinutes(cheapest.durationMins)
      },
      fastest: { 
        price: Math.round(fastest.price), 
        duration: formatDurationMinutes(fastest.durationMins)
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights]);

  // Filter flights
  const filteredFlights = useMemo(() => {
    let result = [...flights];

    // Apply flexible date filter (filter by selected date offset)
    // If selectedDateOffset is null, show ALL flights (no date filtering)
    if (searchParams?.departure_date && selectedDateOffset !== null) {
      const baseDate = new Date(searchParams.departure_date);
      const selectedDate = new Date(baseDate);
      selectedDate.setDate(baseDate.getDate() + selectedDateOffset);
      const selectedDateStr = selectedDate.toISOString().split('T')[0];
      
      result = result.filter(flight => {
        const flightDate = flight.departure_date || (flight.departure_time ? flight.departure_time.split('T')[0] : null);
        return flightDate === selectedDateStr;
      });
    }

    // Apply outbound stops filter
    if (filters.stopsOutbound) {
      result = result.filter(flight => {
        if (filters.stopsOutbound === 'direct') return flight.is_direct || flight.stops === 0;
        if (filters.stopsOutbound === '1') return flight.stops === 1;
        if (filters.stopsOutbound === '2+') return flight.stops >= 2;
        return true;
      });
    }

    // Apply return stops filter
    if (filters.stopsReturn && isRoundTrip) {
      result = result.filter(flight => {
        if (filters.stopsReturn === 'direct') return flight.return_is_direct || flight.return_stops === 0;
        if (filters.stopsReturn === '1') return flight.return_stops === 1;
        if (filters.stopsReturn === '2+') return flight.return_stops >= 2;
        return true;
      });
    }

    // Apply airline filter
    if (filters.airlines.length > 0) {
      result = result.filter(flight => {
        // Check if flight's airline matches any selected airline
        return filters.airlines.some(selectedAirline => {
          // Exact match
          if (flight.airline === selectedAirline) return true;
          // Case-insensitive match
          if (flight.airline && selectedAirline && 
              flight.airline.toLowerCase() === selectedAirline.toLowerCase()) return true;
          return false;
        });
      });
    }

    // Apply fare type filter
    if (filters.fareType) {
      result = result.filter(flight => {
        const fareType = flight.fare_attributes?.fare_type || flight.fare_details?.fare_type || flight.fare_type;
        return fareType === filters.fareType;
      });
    }

    // Apply outbound departure time filter
    if (filters.outboundDepartureTime) {
      result = result.filter(flight => {
        const hour = getHour(flight.departure_time);
        if (filters.outboundDepartureTime === 'morning') return hour >= 5 && hour < 12;
        if (filters.outboundDepartureTime === 'afternoon') return hour >= 12 && hour < 18;
        if (filters.outboundDepartureTime === 'evening') return hour >= 18 || hour < 5;
        return true;
      });
    }

    // Apply outbound arrival time filter
    if (filters.outboundArrivalTime) {
      result = result.filter(flight => {
        const hour = getHour(flight.arrival_time);
        if (filters.outboundArrivalTime === 'morning') return hour >= 5 && hour < 12;
        if (filters.outboundArrivalTime === 'afternoon') return hour >= 12 && hour < 18;
        if (filters.outboundArrivalTime === 'evening') return hour >= 18 || hour < 5;
        return true;
      });
    }

    // Apply return departure time filter
    if (filters.returnDepartureTime && isRoundTrip) {
      result = result.filter(flight => {
        const hour = getHour(flight.return_departure_time);
        if (filters.returnDepartureTime === 'morning') return hour >= 5 && hour < 12;
        if (filters.returnDepartureTime === 'afternoon') return hour >= 12 && hour < 18;
        if (filters.returnDepartureTime === 'evening') return hour >= 18 || hour < 5;
        return true;
      });
    }

    // Apply return arrival time filter
    if (filters.returnArrivalTime && isRoundTrip) {
      result = result.filter(flight => {
        const hour = getHour(flight.return_arrival_time);
        if (filters.returnArrivalTime === 'morning') return hour >= 5 && hour < 12;
        if (filters.returnArrivalTime === 'afternoon') return hour >= 12 && hour < 18;
        if (filters.returnArrivalTime === 'evening') return hour >= 18 || hour < 5;
        return true;
      });
    }

    // Apply connection length outbound filter
    // Apply connection length outbound filter
    if (filters.connectionLengthOutbound) {
      const selectedOption = connectionLengthOptions.find(opt => opt.value === filters.connectionLengthOutbound);
      if (selectedOption) {
        result = result.filter(flight => {
          // No Connections = Direct flights only
          if (selectedOption.value === 'none') {
            return flight.is_direct || flight.stops === 0;
          }
          
          // For connection filters, exclude direct flights and check layover duration
          if (flight.is_direct || flight.stops === 0) {
            return false; // Direct flights don't have connections
          }
          
          // Calculate total layover time from layovers array if available
          let layoverMins = flight.total_layover_minutes || 0;
          
          // If layovers array exists, calculate from it
          if (flight.layovers && flight.layovers.length > 0) {
            layoverMins = flight.layovers.reduce((total, layover) => {
              const duration = layover.duration_minutes || 0;
              return total + duration;
            }, 0);
          }
          
          return layoverMins >= selectedOption.minMinutes && layoverMins <= selectedOption.maxMinutes;
        });
      }
    }

    // Apply connection length return filter
    if (filters.connectionLengthReturn && isRoundTrip) {
      const selectedOption = connectionLengthOptions.find(opt => opt.value === filters.connectionLengthReturn);
      if (selectedOption) {
        result = result.filter(flight => {
          // No Connections = Direct flights only
          if (selectedOption.value === 'none') {
            return flight.return_is_direct || flight.return_stops === 0;
          }
          
          // For connection filters, exclude direct flights and check layover duration
          if (flight.return_is_direct || flight.return_stops === 0) {
            return false; // Direct flights don't have connections
          }
          
          // Calculate total layover time from return_layovers array if available
          let layoverMins = flight.return_total_layover_minutes || 0;
          
          // If return_layovers array exists, calculate from it
          if (flight.return_layovers && flight.return_layovers.length > 0) {
            layoverMins = flight.return_layovers.reduce((total, layover) => {
              const duration = layover.duration_minutes || 0;
              return total + duration;
            }, 0);
          }
          
          return layoverMins >= selectedOption.minMinutes && layoverMins <= selectedOption.maxMinutes;
        });
      }
    }

    // Apply baggage filter - Sort matching flights to top (don't filter out)
    // IMPORTANT: 0 x Hold Luggage is completely isolated from checked baggage options
    if (filters.baggage) {
      result = result.map(flight => {
        // Get baggage info from flight data
        // baggage_included === false means NO checked baggage (hand baggage only)
        // baggage_count > 0 means has checked baggage pieces
        const baggageIncluded = flight.baggage_included !== false;
        const baggageCount = flight.baggage_count ?? flight.hold_baggage_count ?? 0;
        const baggageWeightStr = String(flight.baggage_weight || flight.hold_baggage_weight || '').toLowerCase();
        const baggageWeight = parseInt(baggageWeightStr) || 23;
        
        // Hand baggage only = baggage_included is false OR baggage_count is 0
        const isHandBaggageOnly = !baggageIncluded || baggageCount === 0;
        
        // Has checked baggage = baggage_included is true AND baggage_count > 0
        const hasCheckedBaggage = baggageIncluded && baggageCount > 0;
        
        let baggageMatch = false;
        
        // 0 x Hold Luggage - ONLY match flights with NO checked baggage (hand baggage only)
        if (filters.baggage === 'handonly') {
          baggageMatch = isHandBaggageOnly;
        }
        // 1 x Hold Luggage (any weight) - matches flights with at least 1 piece of checked baggage
        else if (filters.baggage === '1piece') {
          baggageMatch = hasCheckedBaggage && baggageCount >= 1;
        }
        // 1 x Hold Luggage with specific weight (23kg, 25kg, 30kg, 32kg, 35kg, 40kg)
        else if (filters.baggage.startsWith('1piece-')) {
          const requestedWeight = parseInt(filters.baggage.split('-')[1]);
          baggageMatch = hasCheckedBaggage && baggageCount >= 1 && baggageWeight >= requestedWeight;
        }
        // 2 x Hold Luggage - ONLY match flights with 2+ pieces of checked baggage
        else if (filters.baggage === '2piece') {
          baggageMatch = hasCheckedBaggage && baggageCount >= 2;
        }
        
        return { ...flight, _baggageMatch: baggageMatch };
      });
      
      // Sort: matching baggage first, then by price within each group
      result.sort((a, b) => {
        // Priority 1: Baggage match
        if (a._baggageMatch && !b._baggageMatch) return -1;
        if (!a._baggageMatch && b._baggageMatch) return 1;
        // Priority 2: Price
        return (a.price || 0) - (b.price || 0);
      });
    }

    // Apply arrival airport filter
    if (filters.arrivalAirport && filters.arrivalAirport.length > 0) {
      result = result.filter(flight => filters.arrivalAirport.includes(flight.to));
    }

    // Apply connecting airport filter
    if (filters.connectingIn && filters.connectingIn.length > 0) {
      result = result.filter(flight => {
        // Get all connecting airports for this flight (outbound + return)
        const connectingAirports = new Set();
        
        if (flight.layovers) {
          flight.layovers.forEach(layover => {
            if (layover.airport) connectingAirports.add(layover.airport);
          });
        }
        if (flight.return_layovers) {
          flight.return_layovers.forEach(layover => {
            if (layover.airport) connectingAirports.add(layover.airport);
          });
        }
        
        // Check if any selected connecting airport matches
        return filters.connectingIn.some(airport => connectingAirports.has(airport));
      });
    }

    // NEW FILTERS

    // Apply flight duration filter
    if (filters.flightDuration) {
      result = result.filter(flight => {
        const durationMins = parseDuration(flight.duration);
        const option = flightDurationOptions.find(opt => opt.value === filters.flightDuration);
        if (!option) return true;
        
        if (option.minMinutes && durationMins < option.minMinutes) return false;
        if (option.maxMinutes && durationMins > option.maxMinutes) return false;
        return true;
      });
    }

    // Apply overnight flights filter
    if (filters.overnightFlights && filters.overnightFlights !== 'include') {
      result = result.filter(flight => {
        const depHour = getHour(flight.departure_time);
        const isOvernight = depHour >= 22 || depHour < 5;
        
        if (filters.overnightFlights === 'exclude') return !isOvernight;
        if (filters.overnightFlights === 'only') return isOvernight;
        return true;
      });
    }

    // Apply refundable filter
    if (filters.refundable && filters.refundable !== 'all') {
      result = result.filter(flight => {
        const isRefundable = flight.refundable || flight.is_refundable || false;
        if (filters.refundable === 'refundable') return isRefundable;
        if (filters.refundable === 'non-refundable') return !isRefundable;
        return true;
      });
    }

    // Apply flexible tickets filter
    if (filters.flexible && filters.flexible !== 'all') {
      result = result.filter(flight => {
        const isFlexible = flight.flexible || flight.is_flexible || flight.changeable || false;
        if (filters.flexible === 'flexible') return isFlexible;
        if (filters.flexible === 'fixed') return !isFlexible;
        return true;
      });
    }

    // Apply alliance filter
    if (filters.alliance && filters.alliance.length > 0) {
      result = result.filter(flight => {
        const airlineCode = flight.airline_code || flight.airline;
        return filters.alliance.some(alliance => 
          AIRLINE_ALLIANCES[alliance]?.includes(airlineCode)
        );
      });
    }

    // Apply cabin class filter
    if (filters.cabinClass) {
      result = result.filter(flight => {
        const cabin = (flight.cabin_class || flight.travel_class || 'economy').toLowerCase();
        if (filters.cabinClass === 'economy') return cabin.includes('economy') && !cabin.includes('premium');
        if (filters.cabinClass === 'premium') return cabin.includes('premium');
        if (filters.cabinClass === 'business') return cabin.includes('business');
        if (filters.cabinClass === 'first') return cabin.includes('first');
        return true;
      });
    }

    // Apply WiFi filter
    if (filters.wifiAvailable) {
      result = result.filter(flight => flight.wifi || flight.wifi_available || flight.has_wifi);
    }

    // Apply entertainment filter
    if (filters.entertainmentAvailable) {
      result = result.filter(flight => flight.entertainment || flight.ife || flight.has_entertainment);
    }

    // Apply meal filter
    if (filters.mealIncluded) {
      result = result.filter(flight => flight.meal || flight.meal_included || flight.has_meal);
    }

    // Apply aircraft type filter
    if (filters.aircraftType && filters.aircraftType !== 'all') {
      result = result.filter(flight => {
        const aircraft = (flight.aircraft || flight.equipment || '').toUpperCase();
        const isWidebody = WIDEBODY_AIRCRAFT.some(code => aircraft.includes(code));
        
        if (filters.aircraftType === 'widebody') return isWidebody;
        if (filters.aircraftType === 'narrowbody') return !isWidebody && aircraft.length > 0;
        return true;
      });
    }

    // Apply price filter
    if (filters.minPrice !== '') {
      const min = parseFloat(filters.minPrice);
      if (!isNaN(min)) {
        result = result.filter(flight => flight.price >= min);
      }
    }
    if (filters.maxPrice !== '') {
      const max = parseFloat(filters.maxPrice);
      if (!isNaN(max)) {
        result = result.filter(flight => flight.price <= max);
      }
    }

    // When connection length filter is active, sort by shortest connection first
    // This prioritizes matching itineraries at the top
    const hasConnectionLengthFilter = filters.connectionLengthOutbound || filters.connectionLengthReturn;
    
    if (hasConnectionLengthFilter) {
      // Calculate layover minutes for each flight for sorting
      result = result.map(flight => {
        let outboundLayover = flight.total_layover_minutes || 0;
        let returnLayover = flight.return_total_layover_minutes || 0;
        
        // Calculate from layovers array if available
        if (flight.layovers && flight.layovers.length > 0) {
          outboundLayover = flight.layovers.reduce((total, layover) => 
            total + (layover.duration_minutes || 0), 0);
        }
        if (flight.return_layovers && flight.return_layovers.length > 0) {
          returnLayover = flight.return_layovers.reduce((total, layover) => 
            total + (layover.duration_minutes || 0), 0);
        }
        
        return {
          ...flight,
          _outboundLayoverMins: outboundLayover,
          _returnLayoverMins: returnLayover,
          _totalLayoverMins: outboundLayover + returnLayover
        };
      });
      
      // Sort by connection length (shortest first) within the filtered results
      result.sort((a, b) => {
        // Direct flights first (0 layover)
        if (a._totalLayoverMins === 0 && b._totalLayoverMins > 0) return -1;
        if (b._totalLayoverMins === 0 && a._totalLayoverMins > 0) return 1;
        
        // Then by shortest layover
        if (a._totalLayoverMins !== b._totalLayoverMins) {
          return a._totalLayoverMins - b._totalLayoverMins;
        }
        
        // If same layover, sort by price
        return (a.price || 0) - (b.price || 0);
      });
    }

    // Sort
    // Helper to check baggage match priority
    const hasBaggagePriority = filters.baggage;
    
    if (sortBy === 'best') {
      // Best: Uses normalized scoring - 55% price, 35% duration, 10% stops
      const prices = result.map(f => f.price || 0);
      const durs = result.map(f => parseDuration(f.duration) || 0).filter(d => d > 0);
      const maxStops = Math.max(...result.map(f => f.stops || 0), 0);
      
      const minP = Math.min(...prices), maxP = Math.max(...prices);
      const minD = durs.length ? Math.min(...durs) : 0;
      const maxD = durs.length ? Math.max(...durs) : 1;
      
      const calcScore = (f) => {
        const p = (maxP !== minP) ? ((f.price || 0) - minP) / (maxP - minP) : 0;
        const dur = parseDuration(f.duration) || minD;
        const d = (maxD !== minD) ? (dur - minD) / (maxD - minD) : 0;
        const s = maxStops ? ((f.stops || 0) / maxStops) : 0;
        return 0.55 * p + 0.35 * d + 0.10 * s;
      };
      
      result.sort((a, b) => {
        // Baggage match priority first
        if (hasBaggagePriority) {
          if (a._baggageMatch && !b._baggageMatch) return -1;
          if (!a._baggageMatch && b._baggageMatch) return 1;
        }
        return calcScore(a) - calcScore(b);
      });
    } else if (sortBy === 'price') {
      result.sort((a, b) => {
        // Baggage match priority first
        if (hasBaggagePriority) {
          if (a._baggageMatch && !b._baggageMatch) return -1;
          if (!a._baggageMatch && b._baggageMatch) return 1;
        }
        return (a.price || 0) - (b.price || 0);
      });
    } else if (sortBy === 'duration') {
      result.sort((a, b) => {
        // Baggage match priority first
        if (hasBaggagePriority) {
          if (a._baggageMatch && !b._baggageMatch) return -1;
          if (!a._baggageMatch && b._baggageMatch) return 1;
        }
        return (parseDuration(a.duration) || 0) - (parseDuration(b.duration) || 0);
      });
    } else if (sortBy === 'departure') {
      result.sort((a, b) => {
        // Baggage match priority first
        if (hasBaggagePriority) {
          if (a._baggageMatch && !b._baggageMatch) return -1;
          if (!a._baggageMatch && b._baggageMatch) return 1;
        }
        const getDepTime = (f) => {
          if (f.departure_datetime) return new Date(f.departure_datetime);
          if (f.departure_date && f.departure_time) {
            return new Date(`${f.departure_date}T${f.departure_time}:00`);
          }
          return new Date(0);
        };
        return getDepTime(a) - getDepTime(b);
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights, filters, sortBy, selectedDateOffset, searchParams]);

  // Get available airlines from all flights
  const availableAirlines = useMemo(() => {
    const airlines = new Set();
    flights.forEach(flight => {
      if (flight.airline) airlines.add(flight.airline);
    });
    return Array.from(airlines).sort();
  }, [flights]);

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration.replace('PT', '').replace('H', 'h ').replace('M', 'm');
  };

  const formatTime = (dateTimeOrTime) => {
    if (!dateTimeOrTime) return '';
    const s = String(dateTimeOrTime);

    // Already HH:MM
    if (/^\d{2}:\d{2}$/.test(s)) return s;

    // Sabre full ISO: YYYY-MM-DDTHH:MM[:SS][±HH:MM | Z]
    // Pull HH:MM straight from after the 'T', ignoring timezone offset so
    // we show LOCAL AIRPORT TIME (matches Sabre Red).
    const iso = s.match(/T(\d{2}):(\d{2})/);
    if (iso) return `${iso[1]}:${iso[2]}`;

    // Legacy: time-only string "HH:MM:SS+04:00" or "13:55:00Z"
    const head = s.match(/^(\d{2}):(\d{2})/);
    if (head) return `${head[1]}:${head[2]}`;

    return s;
  };

  const formatDate = (dateTimeOrDate) => {
    if (!dateTimeOrDate) return '';
    
    // If it's already in YYYY-MM-DD format, parse it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeOrDate)) {
      const date = new Date(dateTimeOrDate + 'T00:00:00');
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
    
    // Try to parse as full datetime
    try {
      const date = new Date(dateTimeOrDate);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      }
    } catch (e) {
      // Ignore parsing errors
    }
    
    return '';
  };

  // Helper function to check if arrival is on a different day than departure
  const isDifferentDay = (depDate, arrDate) => {
    if (!depDate || !arrDate) return false;
    try {
      const dep = new Date(depDate).toDateString();
      const arr = new Date(arrDate).toDateString();
      return dep !== arr;
    } catch {
      return false;
    }
  };

  // Helper function to format flight date
  const formatFlightDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  const totalPassengers = (searchParams?.adults || 1) + (searchParams?.youth || 0) + (searchParams?.children || 0) + (searchParams?.infants || 0);
  // API prices are the TOTAL for all passengers; the toggle decides how the card shows it.
  const cardPrice = (flight) => (priceDisplayMode === 'total'
    ? { price: Math.round(flight.price), caption: `total for ${totalPassengers} passenger${totalPassengers !== 1 ? 's' : ''}` }
    : { price: Math.round(flight.price / totalPassengers), caption: 'per person' });
  const lastOf = (arr) => (Array.isArray(arr) && arr.length ? arr[arr.length - 1] : null);
  const toLeg = (f) => ({
    from: f.from, to: f.to, departure_time: f.departure_time, arrival_time: f.arrival_time, departure_date: f.departure_date,
    arrival_date: f.arrival_date || lastOf(f.segments)?.arrival_date,
    duration: f.duration, stops: f.stops || 0, layovers: f.layovers || [], airline_code: f.airline_code, airline_name: f.airline || f.airline_name,
    flight_number: f.flight_number, segments: f.segments, cabin_code: f.cabin_class || f.travel_class,
  });
  const toReturnLeg = (f) => (f.return_departure_time ? {
    from: f.to, to: f.from, departure_time: f.return_departure_time, arrival_time: f.return_arrival_time, departure_date: f.return_departure_date,
    arrival_date: f.return_arrival_date || lastOf(f.return_segments)?.arrival_date,
    duration: f.return_duration, stops: f.return_stops || 0, layovers: f.return_layovers || [], airline_code: f.return_airline_code_display || f.return_airline_code || f.airline_code,
    flight_number: f.return_flight_number, segments: f.return_segments,
  } : null);

  const toggleAirlineFilter = (airlineName) => {
    setFilters(prev => {
      const airlines = prev.airlines.includes(airlineName)
        ? prev.airlines.filter(a => a !== airlineName)
        : [...prev.airlines, airlineName];
      return { ...prev, airlines };
    });
  };

  const clearAllFilters = () => {
    setFilters({
      fareType: null,
      stopsOutbound: null,
      stopsReturn: null,
      baggage: null,
      airlines: [],
      outboundDepartureTime: null,
      outboundArrivalTime: null,
      returnDepartureTime: null,
      returnArrivalTime: null,
      connectionLengthOutbound: null,
      connectionLengthReturn: null,
      departureAirport: [],
      arrivalAirport: [],
      connectingIn: [],
      minPrice: '',
      maxPrice: '',
      // Additional filters
      flightDuration: null,
      overnightFlights: null,
      refundable: null,
      flexible: null,
      alliance: [],
      preferredAirlines: [],
      cabinClass: null,
      wifiAvailable: null,
      entertainmentAvailable: null,
      mealIncluded: null,
      aircraftType: null
    });
  };

  // Calculate combined price for mixed flights
  const getCombinedPrice = () => {
    if (selectedOutbound && selectedReturn) {
      // For mixed flights, we estimate by using half the price of each
      // since the API returns round-trip prices
      return Math.round((selectedOutbound.price + selectedReturn.price) / 2);
    }
    return null;
  };

  // Calculate active filter count for mobile button
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.fareType) count++;
    if (filters.stopsOutbound) count++;
    if (filters.stopsReturn) count++;
    if (filters.baggage) count++;
    if (filters.airlines.length > 0) count += filters.airlines.length;
    if (filters.outboundDepartureTime) count++;
    if (filters.outboundArrivalTime) count++;
    if (filters.returnDepartureTime) count++;
    if (filters.returnArrivalTime) count++;
    if (filters.connectionLengthOutbound) count++;
    if (filters.connectionLengthReturn) count++;
    if (filters.minPrice) count++;
    if (filters.maxPrice) count++;
    return count;
  }, [filters]);

  // Generate +/- 3 days flexible date options
  // Generate flexible date options - Full calendar month around departure date
  const flexibleDateOptions = useMemo(() => {
    if (!searchParams?.departure_date) return [];
    
    const baseDate = new Date(searchParams.departure_date);
    const returnBaseDate = searchParams?.return_date ? new Date(searchParams.return_date) : null;
    const tripDuration = returnBaseDate ? Math.round((returnBaseDate - baseDate) / (1000 * 60 * 60 * 24)) : 0;
    
    const options = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate dates for the full month around departure date
    // Start from 7 days before or today (whichever is later) and go 30 days after
    const startDate = new Date(baseDate);
    startDate.setDate(baseDate.getDate() - 14); // 14 days before
    
    // Don't go before today
    if (startDate < today) {
      startDate.setTime(today.getTime());
    }
    
    // Generate 45 days of options (covers about 6 weeks)
    for (let i = 0; i < 45; i++) {
      const depDate = new Date(startDate);
      depDate.setDate(startDate.getDate() + i);
      
      // Calculate offset from base date
      const offset = Math.round((depDate - baseDate) / (1000 * 60 * 60 * 24));
      
      const retDate = returnBaseDate ? new Date(depDate) : null;
      if (retDate) {
        retDate.setDate(depDate.getDate() + tripDuration);
      }
      
      // Find cheapest flight for this date from search results
      const dateStr = depDate.toISOString().split('T')[0];
      const flightsForDate = flights.filter(f => {
        const flightDate = f.departure_date || (f.departure_time ? f.departure_time.split('T')[0] : null);
        return flightDate === dateStr;
      });
      
      // Priority: 1) flights from search results, 2) fare calendar prices
      let lowestPrice = null;
      let flightCount = flightsForDate.length;
      
      if (flightsForDate.length > 0) {
        // Use actual search results price
        lowestPrice = Math.min(...flightsForDate.map(f => f.price || Infinity));
      } else if (fareCalendarPrices && fareCalendarPrices[dateStr]) {
        // Use fare calendar price (approximate)
        lowestPrice = fareCalendarPrices[dateStr];
      }
      
      options.push({
        offset: offset,
        departureDate: depDate,
        departureDateStr: dateStr,
        returnDate: retDate,
        returnDateStr: retDate ? retDate.toISOString().split('T')[0] : null,
        dateLabel: `${depDate.getDate()} ${depDate.toLocaleString('en-GB', { month: 'short' })}`,
        dayLabel: depDate.toLocaleString('en-GB', { weekday: 'short' }),
        rangeLabel: retDate 
          ? `${depDate.getDate()} ${depDate.toLocaleString('en-GB', { month: 'short' })} – ${retDate.getDate()} ${retDate.toLocaleString('en-GB', { month: 'short' })}`
          : `${depDate.getDate()} ${depDate.toLocaleString('en-GB', { month: 'short' })}`,
        price: lowestPrice,
        flightCount: flightCount,
        isFromCalendar: lowestPrice && flightCount === 0, // Price from calendar, not search results
        isSelected: selectedDateOffset !== null && offset === selectedDateOffset,
        isLowest: false, // Will be set below
        isWeekend: depDate.getDay() === 0 || depDate.getDay() === 6,
        isCurrentSearch: offset === 0
      });
    }
    
    // Find the lowest price option
    const prices = options.filter(o => o.price).map(o => o.price);
    const lowestPrice = prices.length > 0 ? Math.min(...prices) : null;
    options.forEach(o => {
      if (o.price && o.price === lowestPrice) o.isLowest = true;
    });
    
    return options;
  }, [searchParams, flights, selectedDateOffset, fareCalendarPrices]);

  // Generate 7x7 date matrix for flexible dates grid (outbound x return)
  const dateMatrix = useMemo(() => {
    if (!searchParams?.departure_date || !searchParams?.return_date) return null;
    
    const baseDepDate = new Date(searchParams.departure_date);
    const baseRetDate = new Date(searchParams.return_date);
    
    // Generate 7 departure dates (-3 to +3 days)
    const departureDates = [];
    for (let i = -3; i <= 3; i++) {
      const date = new Date(baseDepDate);
      date.setDate(baseDepDate.getDate() + i);
      departureDates.push({
        offset: i,
        date: date,
        dateStr: date.toISOString().split('T')[0],
        label: `${date.toLocaleString('en-GB', { weekday: 'short' })} ${date.getDate()} ${date.toLocaleString('en-GB', { month: 'short' })}`
      });
    }
    
    // Generate 7 return dates (-3 to +3 days)
    const returnDates = [];
    for (let i = -3; i <= 3; i++) {
      const date = new Date(baseRetDate);
      date.setDate(baseRetDate.getDate() + i);
      returnDates.push({
        offset: i,
        date: date,
        dateStr: date.toISOString().split('T')[0],
        label: `${date.toLocaleString('en-GB', { weekday: 'short' })} ${date.getDate()} ${date.toLocaleString('en-GB', { month: 'short' })}`
      });
    }
    
    // Build price matrix
    const matrix = [];
    let lowestPrice = Infinity;
    let lowestCell = null;
    let currentCell = null;
    
    departureDates.forEach((dep, depIdx) => {
      const row = [];
      returnDates.forEach((ret, retIdx) => {
        // Find flights matching this date combination
        const matchingFlights = flights.filter(f => {
          const flightDepDate = f.departure_date || (f.departure_time ? f.departure_time.split('T')[0] : null);
          const flightRetDate = f.return_departure_date || (f.return_departure_time ? f.return_departure_time.split('T')[0] : null);
          return flightDepDate === dep.dateStr && flightRetDate === ret.dateStr;
        });
        
        const price = matchingFlights.length > 0 
          ? Math.min(...matchingFlights.map(f => f.price || Infinity))
          : null;
        
        const cell = {
          depOffset: dep.offset,
          retOffset: ret.offset,
          depDate: dep.dateStr,
          retDate: ret.dateStr,
          price: price,
          flightCount: matchingFlights.length,
          isCurrent: dep.offset === 0 && ret.offset === 0,
          isLowest: false,
          isWeekend: dep.date.getDay() === 0 || dep.date.getDay() === 6 || ret.date.getDay() === 0 || ret.date.getDay() === 6
        };
        
        if (price && price < lowestPrice) {
          lowestPrice = price;
          lowestCell = cell;
        }
        
        if (cell.isCurrent) {
          currentCell = cell;
        }
        
        row.push(cell);
      });
      matrix.push(row);
    });
    
    // Mark lowest price cell
    if (lowestCell) {
      lowestCell.isLowest = true;
    }
    
    return {
      departureDates,
      returnDates,
      matrix,
      lowestPrice: lowestPrice === Infinity ? null : lowestPrice,
      currentPrice: currentCell?.price || null
    };
  }, [searchParams, flights]);

  // If flexible dates, show matrix view
  if (isFlexible && (flights.length > 0 || pinnedFlights.length > 0) && !isLoading) {
    // The grid must show EVERY flight BFM returned — including the pinned
    // preferred-airline direct flights that are split out of `flights`.
    const seen = new Set();
    const allFlexFlights = [...pinnedFlights, ...flights].filter((f) => {
      const key = `${f.id || `${f.airline_code}${f.flight_number}`}_${f.departure_date}_${f.return_departure_date || ''}_${f.return_flight_number || ''}_${f.price}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return <FlexibleDatesMatrix flights={allFlexFlights} searchParams={searchParams} onSelectFlight={onSelectFlight} onModifySearch={onModifySearch} onSearchDates={onInlineSearch} isRevalidating={isRevalidating} priceDisplayMode={priceDisplayMode} />;
  }

  // Filter Section Component (uses parent's expandedSections state)
  const FilterSection = ({ title, name, children }) => (
    <div className="mb-3">
      <button 
        onClick={() => toggleSection(name)}
        className="w-full flex items-center justify-between py-2 text-left font-semibold text-slate-800 hover:text-brand-600"
      >
        {title}
        {expandedSections[name] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {expandedSections[name] && <div className="mt-2">{children}</div>}
    </div>
  );

  // Render filter content (used in both desktop sidebar and mobile sheet)
  const renderFilterContent = () => (
    <>
      {/* Fare Type Filter */}
      <FilterSection title={`Fare Type${uniqueFareTypes.length > 0 ? ` (${uniqueFareTypes.length})` : ''}`} name="fareType">
        {uniqueFareTypes.length > 0 ? (
          <RadioGroup 
            value={filters.fareType || ''} 
            onValueChange={(value) => {
              setFilters(prev => ({ ...prev, fareType: value || null }));
              // Scroll to show filtered results at top
              if (value) setTimeout(() => scrollToFlightResults(), 100);
            }}
          >
            {uniqueFareTypes.map(fareType => (
              <div key={fareType} className="flex items-center space-x-2 py-1.5">
                <RadioGroupItem value={fareType} id={`m-fare-${fareType}`} />
                <Label htmlFor={`m-fare-${fareType}`} className="text-sm cursor-pointer">
                  {fareType}
                </Label>
              </div>
            ))}
          </RadioGroup>
        ) : (
          <p className="text-sm text-gray-500">No fare type data available</p>
        )}
      </FilterSection>

      <Separator className="my-3" />

      {/* Stops from Origin Filter */}
      <FilterSection title={`Stops from ${searchParams?.origin || 'Origin'}`} name="stopsOutbound">
        <RadioGroup 
          value={filters.stopsOutbound || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, stopsOutbound: value || null }))}
          data-testid="stops-outbound-filter"
        >
          {stopOptions.map(option => (
            <div key={option.value} className="flex items-center justify-between py-1.5">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={`m-stopOut-${option.value}`} data-testid={`stop-outbound-${option.value}`} />
                <Label htmlFor={`m-stopOut-${option.value}`} className="text-sm cursor-pointer">
                  {option.label}
                </Label>
              </div>
              <div className="text-right">
                {option.minPrice && (
                  <span className="text-sm font-medium text-brand-600"><Money gbp={Math.round(option.minPrice)} /></span>
                )}
                <span className="text-xs text-slate-400 ml-1">({option.count})</span>
              </div>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      {isRoundTrip && (
        <>
          <Separator className="my-3" />
          <FilterSection title={`Stops from ${searchParams?.destination || 'Destination'}`} name="stopsReturn">
            <RadioGroup 
              value={filters.stopsReturn || ''} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, stopsReturn: value || null }))}
            >
              {stopOptions.map(option => (
                <div key={option.value} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value={option.value} id={`m-stopRet-${option.value}`} />
                    <Label htmlFor={`m-stopRet-${option.value}`} className="text-sm cursor-pointer">
                      {option.label}
                    </Label>
                  </div>
                  <div className="text-right">
                    {option.minPrice && (
                      <span className="text-sm font-medium text-brand-600"><Money gbp={Math.round(option.minPrice)} /></span>
                    )}
                    <span className="text-xs text-slate-400 ml-1">({option.count})</span>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </FilterSection>
        </>
      )}

      <Separator className="my-3" />

      {/* Baggage Filter */}
      <FilterSection title="Baggage" name="baggage">
        <div data-testid="baggage-filter" className="space-y-1">
          {/* 0 × Hold Luggage */}
          <div 
            className={`flex items-center justify-between py-2.5 px-3 rounded-lg cursor-pointer transition-all ${
              filters.baggage === 'handonly' ? 'bg-brand-50 border border-brand-300' : 'hover:bg-slate-50'
            }`}
            onClick={() => setFilters(prev => ({ ...prev, baggage: prev.baggage === 'handonly' ? null : 'handonly' }))}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                filters.baggage === 'handonly' ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
              }`}>
                {filters.baggage === 'handonly' && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
              <span className="text-sm font-medium text-slate-800">0 × Hold Luggage</span>
            </div>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{baggageCounts.count0}</span>
          </div>
          
          {/* 1 × Hold Luggage - Expandable */}
          <div className={`rounded-lg transition-all ${
            filters.baggage && filters.baggage.startsWith('1piece') ? 'bg-brand-50 border border-brand-300' : ''
          }`}>
            <div 
              className={`flex items-center justify-between py-2.5 px-3 cursor-pointer transition-all ${
                filters.baggage && filters.baggage.startsWith('1piece') ? '' : 'hover:bg-slate-50 rounded-lg'
              }`}
              onClick={() => setFilters(prev => ({ 
                ...prev, 
                baggage: prev.baggage && prev.baggage.startsWith('1piece') ? null : '1piece' 
              }))}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  filters.baggage && filters.baggage.startsWith('1piece') ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
                }`}>
                  {filters.baggage && filters.baggage.startsWith('1piece') && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
                <span className="text-sm font-medium text-slate-800">1 × Hold Luggage</span>
              </div>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{baggageCounts.count1}</span>
            </div>
            
            {/* Weight Sub-options - Show when 1piece is selected */}
            {filters.baggage && filters.baggage.startsWith('1piece') && (
              <div className="pl-8 pr-3 pb-3 space-y-1 border-l-2 border-brand-200 ml-5 mt-1">
                {[23, 25, 30, 32, 35, 40].map((weight) => (
                  <div 
                    key={weight}
                    className={`flex items-center space-x-2 py-1.5 px-2 rounded cursor-pointer transition-all ${
                      filters.baggage === `1piece-${weight}` ? 'bg-brand-100' : 'hover:bg-slate-50'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters(prev => ({ 
                        ...prev, 
                        baggage: prev.baggage === `1piece-${weight}` ? '1piece' : `1piece-${weight}` 
                      }));
                    }}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      filters.baggage === `1piece-${weight}` ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
                    }`}>
                      {filters.baggage === `1piece-${weight}` && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                    </div>
                    <span className="text-sm text-slate-700">{weight} kg</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* 2 × Hold Luggage */}
          <div 
            className={`flex items-center justify-between py-2.5 px-3 rounded-lg cursor-pointer transition-all ${
              filters.baggage === '2piece' ? 'bg-brand-50 border border-brand-300' : 'hover:bg-slate-50'
            }`}
            onClick={() => setFilters(prev => ({ ...prev, baggage: prev.baggage === '2piece' ? null : '2piece' }))}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                filters.baggage === '2piece' ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
              }`}>
                {filters.baggage === '2piece' && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
              <span className="text-sm font-medium text-slate-800">2 × Hold Luggage <span className="text-slate-500 font-normal">(2×23 = 46 kg)</span></span>
            </div>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{baggageCounts.count2}</span>
          </div>
        </div>
      </FilterSection>

      <Separator className="my-3" />

      {/* Airline Filter */}
      <FilterSection title={`Airline (${uniqueAirlines.length})`} name="airlines">
        <div className="space-y-2">
          {uniqueAirlines.map(airline => (
            <div key={airline.name} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`m-airline-${airline.name}`}
                  checked={filters.airlines.includes(airline.name)}
                  onCheckedChange={(checked) => {
                    setFilters(prev => ({
                      ...prev,
                      airlines: checked 
                        ? [...prev.airlines, airline.name]
                        : prev.airlines.filter(a => a !== airline.name)
                    }));
                    // Scroll to show filtered results at top when selecting
                    if (checked) {
                      setTimeout(() => scrollToFlightResults(), 100);
                    }
                  }}
                  data-testid={`airline-filter-${airline.name}`}
                />
                <Label htmlFor={`m-airline-${airline.name}`} className="text-sm cursor-pointer flex items-center gap-2">
                  <AirlineLogo code={airline.code} className="w-6 h-6" />
                  {airline.name}
                </Label>
              </div>
              <span className="text-sm font-medium text-brand-600"><Money gbp={Math.round(airline.minPrice)} /></span>
            </div>
          ))}
        </div>
      </FilterSection>

      <Separator className="my-3" />

      {/* Outbound Departure Time Filter */}
      <FilterSection title="Outbound Departure Time" name="outboundDepartureTime">
        <RadioGroup 
          value={filters.outboundDepartureTime || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, outboundDepartureTime: value || null }))}
        >
          {timeOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-outDep-${option.value}`} />
              <Label htmlFor={`m-outDep-${option.value}`} className="text-sm cursor-pointer">
                {option.label} <span className="text-slate-500 text-xs">({option.time})</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      <Separator className="my-3" />

      {/* Outbound Arrival Time Filter */}
      <FilterSection title="Outbound Arrival Time" name="outboundArrivalTime">
        <RadioGroup 
          value={filters.outboundArrivalTime || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, outboundArrivalTime: value || null }))}
        >
          {timeOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-outArr-${option.value}`} />
              <Label htmlFor={`m-outArr-${option.value}`} className="text-sm cursor-pointer">
                {option.label} <span className="text-slate-500 text-xs">({option.time})</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      {isRoundTrip && (
        <>
          <Separator className="my-3" />

          {/* Return Departure Time Filter */}
          <FilterSection title="Return Departure Time" name="returnDepartureTime">
            <RadioGroup 
              value={filters.returnDepartureTime || ''} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, returnDepartureTime: value || null }))}
            >
              {timeOptions.map(option => (
                <div key={option.value} className="flex items-center space-x-2 py-1.5">
                  <RadioGroupItem value={option.value} id={`m-retDep-${option.value}`} />
                  <Label htmlFor={`m-retDep-${option.value}`} className="text-sm cursor-pointer">
                    {option.label} <span className="text-slate-500 text-xs">({option.time})</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </FilterSection>

          <Separator className="my-3" />

          {/* Return Arrival Time Filter */}
          <FilterSection title="Return Arrival Time" name="returnArrivalTime">
            <RadioGroup 
              value={filters.returnArrivalTime || ''} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, returnArrivalTime: value || null }))}
            >
              {timeOptions.map(option => (
                <div key={option.value} className="flex items-center space-x-2 py-1.5">
                  <RadioGroupItem value={option.value} id={`m-retArr-${option.value}`} />
                  <Label htmlFor={`m-retArr-${option.value}`} className="text-sm cursor-pointer">
                    {option.label} <span className="text-slate-500 text-xs">({option.time})</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </FilterSection>
        </>
      )}

      <Separator className="my-3" />

      {/* Departure Airport Filter */}
      <FilterSection title={`Departure Airport${uniqueDepartureAirports.length > 0 ? ` (${uniqueDepartureAirports.length})` : ''}`} name="departureAirport">
        {uniqueDepartureAirports.length > 0 ? (
          <div className="space-y-2">
            {uniqueDepartureAirports.map(airport => (
              <div key={airport} className="flex items-center space-x-2">
                <Checkbox
                  id={`m-depAirport-${airport}`}
                  checked={filters.departureAirport.includes(airport)}
                  onCheckedChange={(checked) => {
                    setFilters(prev => ({
                      ...prev,
                      departureAirport: checked 
                        ? [...prev.departureAirport, airport]
                        : prev.departureAirport.filter(a => a !== airport)
                    }));
                  }}
                />
                <Label htmlFor={`m-depAirport-${airport}`} className="text-sm cursor-pointer">
                  {airport}
                </Label>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Single departure airport</p>
        )}
      </FilterSection>

      <Separator className="my-3" />

      {/* Connection Length Filter - Outbound */}
      <FilterSection title={`Connection Length ${searchParams?.origin || ''} - ${searchParams?.destination || ''}`} name="connectionLengthOutbound">
        <RadioGroup 
          value={filters.connectionLengthOutbound || ''} 
          onValueChange={(value) => {
            setFilters(prev => ({ ...prev, connectionLengthOutbound: value || null }));
            // Scroll to show filtered results at top
            if (value) setTimeout(() => scrollToFlightResults(), 100);
          }}
        >
          {connectionLengthOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-connOut-${option.value}`} />
              <Label htmlFor={`m-connOut-${option.value}`} className="text-sm cursor-pointer">
                {option.label} {option.time && <span className="text-slate-500 text-xs">({option.time})</span>}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      {isRoundTrip && (
        <>
          <Separator className="my-3" />

          {/* Connection Length Filter - Return */}
          <FilterSection title={`Connection Length ${searchParams?.destination || ''} - ${searchParams?.origin || ''}`} name="connectionLengthReturn">
            <RadioGroup 
              value={filters.connectionLengthReturn || ''} 
              onValueChange={(value) => {
                setFilters(prev => ({ ...prev, connectionLengthReturn: value || null }));
                // Scroll to show filtered results at top
                if (value) setTimeout(() => scrollToFlightResults(), 100);
              }}
            >
              {connectionLengthOptions.map(option => (
                <div key={option.value} className="flex items-center space-x-2 py-1.5">
                  <RadioGroupItem value={option.value} id={`m-connRet-${option.value}`} />
                  <Label htmlFor={`m-connRet-${option.value}`} className="text-sm cursor-pointer">
                    {option.label} {option.time && <span className="text-slate-500 text-xs">({option.time})</span>}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </FilterSection>
        </>
      )}

      {/* Connecting In Filter */}
      <Separator className="my-3" />

      <FilterSection title={`Connecting In${uniqueConnectingAirports.length > 0 ? ` (${uniqueConnectingAirports.length})` : ''}`} name="connectingIn">
        {uniqueConnectingAirports.length > 0 ? (
          <div className="space-y-2">
            {uniqueConnectingAirports.map(airport => (
              <div key={airport} className="flex items-center space-x-2">
                <Checkbox
                  id={`m-connIn-${airport}`}
                  checked={filters.connectingIn.includes(airport)}
                  onCheckedChange={(checked) => {
                    setFilters(prev => ({
                      ...prev,
                      connectingIn: checked 
                        ? [...prev.connectingIn, airport]
                        : prev.connectingIn.filter(a => a !== airport)
                    }));
                    // Scroll to show filtered results at top when selecting
                    if (checked) {
                      setTimeout(() => scrollToFlightResults(), 100);
                    }
                  }}
                />
                <Label htmlFor={`m-connIn-${airport}`} className="text-sm cursor-pointer">
                  {airport}
                </Label>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Direct flights only</p>
        )}
      </FilterSection>

      <Separator className="my-3" />

      {/* NEW FILTERS SECTION */}
      
      {/* Flight Duration Filter */}
      <FilterSection title="Flight Duration" name="flightDuration">
        <RadioGroup 
          value={filters.flightDuration || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, flightDuration: value || null }))}
        >
          {flightDurationOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-duration-${option.value}`} />
              <Label htmlFor={`m-duration-${option.value}`} className="text-sm cursor-pointer flex items-center gap-2">
                <Timer className="h-3.5 w-3.5 text-slate-400" />
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      <Separator className="my-3" />

      {/* Overnight Flights Filter */}
      <FilterSection title="Overnight Flights" name="overnightFlights">
        <RadioGroup 
          value={filters.overnightFlights || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, overnightFlights: value || null }))}
        >
          {overnightOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-overnight-${option.value}`} />
              <Label htmlFor={`m-overnight-${option.value}`} className="text-sm cursor-pointer flex items-center gap-2">
                <Moon className="h-3.5 w-3.5 text-slate-400" />
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      <Separator className="my-3" />

      {/* Arrival Airport Filter */}
      <FilterSection title={`Arrival Airport${uniqueArrivalAirports.length > 0 ? ` (${uniqueArrivalAirports.length})` : ''}`} name="arrivalAirport">
        {uniqueArrivalAirports.length > 0 ? (
          <div className="space-y-2">
            {uniqueArrivalAirports.map(airport => (
              <div key={airport} className="flex items-center space-x-2">
                <Checkbox
                  id={`m-arrAirport-${airport}`}
                  checked={filters.arrivalAirport.includes(airport)}
                  onCheckedChange={(checked) => {
                    setFilters(prev => ({
                      ...prev,
                      arrivalAirport: checked 
                        ? [...prev.arrivalAirport, airport]
                        : prev.arrivalAirport.filter(a => a !== airport)
                    }));
                  }}
                />
                <Label htmlFor={`m-arrAirport-${airport}`} className="text-sm cursor-pointer">
                  {airport}
                </Label>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Single arrival airport</p>
        )}
      </FilterSection>

      <Separator className="my-3" />

      {/* Alliance Filter */}
      <FilterSection title="Airline Alliance" name="alliance">
        {availableAlliances.length > 0 ? (
          <div className="space-y-2">
            {availableAlliances.map(alliance => (
              <div key={alliance.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`m-alliance-${alliance.name}`}
                    checked={filters.alliance.includes(alliance.name)}
                    onCheckedChange={(checked) => {
                      setFilters(prev => ({
                        ...prev,
                        alliance: checked 
                          ? [...prev.alliance, alliance.name]
                          : prev.alliance.filter(a => a !== alliance.name)
                      }));
                    }}
                  />
                  <Label htmlFor={`m-alliance-${alliance.name}`} className="text-sm cursor-pointer flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5 text-slate-400" />
                    {alliance.name}
                  </Label>
                </div>
                <span className="text-xs text-slate-400">({alliance.count})</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No alliance data available</p>
        )}
      </FilterSection>

      <Separator className="my-3" />

      {/* Cabin Class Filter */}
      <FilterSection title="Cabin Class" name="cabinClass">
        <RadioGroup 
          value={filters.cabinClass || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, cabinClass: value || null }))}
        >
          {cabinClassOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-cabin-${option.value}`} />
              <Label htmlFor={`m-cabin-${option.value}`} className="text-sm cursor-pointer flex items-center gap-2">
                <Armchair className="h-3.5 w-3.5 text-slate-400" />
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      <Separator className="my-3" />

      {/* Refundable Tickets Filter */}
      <FilterSection title="Ticket Flexibility" name="refundable">
        <RadioGroup 
          value={filters.refundable || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, refundable: value || null }))}
        >
          {refundableOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-refund-${option.value}`} />
              <Label htmlFor={`m-refund-${option.value}`} className="text-sm cursor-pointer flex items-center gap-2">
                <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      <Separator className="my-3" />

      {/* Flexible Tickets Filter */}
      <FilterSection title="Changeable Tickets" name="flexible">
        <RadioGroup 
          value={filters.flexible || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, flexible: value || null }))}
        >
          {flexibleOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-flex-${option.value}`} />
              <Label htmlFor={`m-flex-${option.value}`} className="text-sm cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      <Separator className="my-3" />

      {/* Amenities Filter */}
      <FilterSection title="Onboard Amenities" name="amenities">
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="m-wifi"
              checked={filters.wifiAvailable || false}
              onCheckedChange={(checked) => setFilters(prev => ({ ...prev, wifiAvailable: checked }))}
            />
            <Label htmlFor="m-wifi" className="text-sm cursor-pointer flex items-center gap-2">
              <Wifi className="h-3.5 w-3.5 text-blue-500" />
              WiFi Available
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="m-entertainment"
              checked={filters.entertainmentAvailable || false}
              onCheckedChange={(checked) => setFilters(prev => ({ ...prev, entertainmentAvailable: checked }))}
            />
            <Label htmlFor="m-entertainment" className="text-sm cursor-pointer flex items-center gap-2">
              <Tv className="h-3.5 w-3.5 text-purple-500" />
              In-flight Entertainment
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="m-meal"
              checked={filters.mealIncluded || false}
              onCheckedChange={(checked) => setFilters(prev => ({ ...prev, mealIncluded: checked }))}
            />
            <Label htmlFor="m-meal" className="text-sm cursor-pointer flex items-center gap-2">
              <UtensilsCrossed className="h-3.5 w-3.5 text-orange-500" />
              Meal Included
            </Label>
          </div>
        </div>
      </FilterSection>

      <Separator className="my-3" />

      {/* Aircraft Type Filter */}
      <FilterSection title="Aircraft Type" name="aircraftType">
        <RadioGroup 
          value={filters.aircraftType || ''} 
          onValueChange={(value) => setFilters(prev => ({ ...prev, aircraftType: value || null }))}
        >
          {aircraftTypeOptions.map(option => (
            <div key={option.value} className="flex items-center space-x-2 py-1.5">
              <RadioGroupItem value={option.value} id={`m-aircraft-${option.value}`} />
              <Label htmlFor={`m-aircraft-${option.value}`} className="text-sm cursor-pointer flex items-center gap-2">
                <Plane className="h-3.5 w-3.5 text-slate-400" />
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </FilterSection>

      <Separator className="my-3" />

      {/* Price Range Filter */}
      <FilterSection title="Total Price" name="price">
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <Label className="text-xs text-slate-500">Min (£)</Label>
            <Input
              type="number"
              placeholder="0"
              value={filters.minPrice}
              onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
              className="h-9"
            />
          </div>
          <div className="flex-1">
            <Label className="text-xs text-slate-500">Max (£)</Label>
            <Input
              type="number"
              placeholder="Any"
              value={filters.maxPrice}
              onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
              className="h-9"
            />
          </div>
        </div>
      </FilterSection>
    </>
  );

  // Function to adjust dates by one day
  const adjustDate = (dateType, direction) => {
    const currentDate = dateType === 'departure' 
      ? (searchParams?.departure_date ? new Date(searchParams.departure_date) : new Date())
      : (searchParams?.return_date ? new Date(searchParams.return_date) : new Date());
    
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    
    // Don't allow past dates for departure
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateType === 'departure' && newDate < today) return;
    
    // Don't allow return before departure
    if (dateType === 'return' && searchParams?.departure_date) {
      const depDate = new Date(searchParams.departure_date);
      if (newDate < depDate) return;
    }
    
    // Don't allow departure after return
    if (dateType === 'departure' && searchParams?.return_date) {
      const retDate = new Date(searchParams.return_date);
      if (newDate > retDate) return;
    }
    
    // Trigger a new search with the updated date
    if (onModifySearch) {
      // For now, just show that the arrows work - actual date change would need backend call
      console.log(`Adjusting ${dateType} date by ${direction} day(s)`);
    }
  };

  return (
    <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-8">
      {/* SEARCH INFO BAR - Orange theme */}
      <div className={`bg-[#FF6600] text-white shadow-xl px-3 sm:px-4 py-3 ${showInlineSearch ? 'rounded-t-xl' : 'rounded-t-xl'}`}>
        {/* Mobile Layout - Centered */}
        <div className="flex flex-col gap-3 lg:hidden">
          {/* Top row - Centered search button and route info */}
          <div className="flex items-center justify-center gap-3">
            <button 
              onClick={() => setShowInlineSearch(!showInlineSearch)}
              data-testid="modify-search-btn-mobile"
              className={`p-2.5 rounded-full transition-all flex-shrink-0 ${
                showInlineSearch 
                  ? 'bg-white text-[#FF6600]' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {showInlineSearch ? (
                <X className="w-5 h-5" />
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </button>
            
            <button 
              onClick={() => setShowInlineSearch(!showInlineSearch)}
              className="text-left min-w-0"
              data-testid="modify-search-summary-mobile"
            >
              <div className="text-white font-semibold text-sm">
                {searchParams?.origin || 'LON'} → {searchParams?.destination || 'DXB'}
              </div>
              <div className="text-white/90 text-xs">
                {(() => {
                  const adults = searchParams?.adults || 1;
                  const youth = searchParams?.youth || 0;
                  const children = searchParams?.children || 0;
                  const infants = searchParams?.infants || 0;
                  const total = adults + youth + children + infants;
                  return `${total} passenger${total > 1 ? 's' : ''}`;
                })()} · {searchParams?.cabin_class === 'Y' ? 'Economy' : searchParams?.cabin_class === 'C' ? 'Business' : 'Economy'}
              </div>
            </button>
            
          </div>
          
          {/* Bottom row - Dates (white pills for contrast on the orange bar) */}
          <div className="flex items-center justify-center gap-2 flex-wrap" data-testid="mobile-date-stepper">
            {[
              { type: 'departure', label: 'Depart', value: searchParams?.departure_date },
              ...(searchParams?.return_date ? [{ type: 'return', label: 'Return', value: searchParams.return_date }] : []),
            ].map(({ type, label, value }) => {
              const d = value ? new Date(value) : null;
              const text = d
                ? `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`
                : label;
              return (
                <div key={type} className="flex items-center bg-white rounded-lg shadow-sm">
                  <button
                    onClick={() => adjustDate(type, -1)}
                    aria-label={`Previous ${label.toLowerCase()} day`}
                    data-testid={`mobile-${type}-prev-day`}
                    className="p-2 hover:bg-[#F5F5F5] rounded-l-lg transition-colors"
                  >
                    <ChevronUp className="w-5 h-5 text-[#FF6600] rotate-[-90deg]" />
                  </button>
                  <div className="px-2 py-1 min-w-[96px] text-center leading-tight">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
                    <div className="text-[#222222] text-sm font-bold" data-testid={`mobile-${type}-date`}>{text}</div>
                  </div>
                  <button
                    onClick={() => adjustDate(type, 1)}
                    aria-label={`Next ${label.toLowerCase()} day`}
                    data-testid={`mobile-${type}-next-day`}
                    className="p-2 hover:bg-[#F5F5F5] rounded-r-lg transition-colors"
                  >
                    <ChevronDown className="w-5 h-5 text-[#FF6600] rotate-[-90deg]" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop Layout - Single Row */}
        <div className="hidden lg:flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setShowInlineSearch(!showInlineSearch)}
              data-testid="modify-search-btn"
              className={`p-3 rounded-xl transition-all shadow-lg hover:shadow-xl ${
                showInlineSearch 
                  ? 'bg-white text-[#FF6600] hover:bg-gray-100' 
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
              title={showInlineSearch ? "Close search" : "Modify search"}
            >
              {showInlineSearch ? (
                <X className="w-5 h-5" />
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </button>
            
            <button 
              onClick={() => setShowInlineSearch(!showInlineSearch)}
              className="text-white text-left hover:text-white/80 transition-colors cursor-pointer"
            >
              <span className="font-medium">
                {searchParams?.origin || 'London'} → {searchParams?.destination || 'Dubai'} · {(() => {
                  const adults = searchParams?.adults || 1;
                  const youth = searchParams?.youth || 0;
                  const children = searchParams?.children || 0;
                  const infants = searchParams?.infants || 0;
                  const total = adults + youth + children + infants;
                  const parts = [];
                  if (adults > 0) parts.push(`${adults} adult${adults > 1 ? 's' : ''}`);
                  if (youth > 0) parts.push(`${youth} youth`);
                  if (children > 0) parts.push(`${children} child${children > 1 ? 'ren' : ''}`);
                  if (infants > 0) parts.push(`${infants} infant${infants > 1 ? 's' : ''}`);
                  return parts.join(', ') || '1 adult';
                })()}, {searchParams?.cabin_class === 'Y' ? 'Economy' : searchParams?.cabin_class === 'C' ? 'Business' : searchParams?.cabin_class || 'Economy'}
              </span>
            </button>
            
          </div>
          
          <div className="flex items-center gap-1">
            <div className="flex items-center bg-white rounded-lg shadow-sm">
              <button 
                onClick={() => adjustDate('departure', -1)}
                className="p-2 hover:bg-[#F5F5F5] rounded-l-lg transition-colors"
              >
                <ChevronUp className="w-5 h-5 text-[#FF6600] rotate-[-90deg]" />
              </button>
              <div className="px-3 py-1.5 min-w-[90px] text-center">
                <span className="font-medium text-[#222222] text-sm">
                  {searchParams?.departure_date 
                    ? (() => {
                        const d = new Date(searchParams.departure_date);
                        const weekday = d.toLocaleDateString('en-GB', { weekday: 'short' });
                        const day = d.getDate();
                        const month = d.toLocaleDateString('en-GB', { month: 'short' });
                        return `${weekday}, ${day} ${month}`;
                      })()
                    : 'Tue, 6 Jan'}
                </span>
              </div>
              <button 
                onClick={() => adjustDate('departure', 1)}
                className="p-2 hover:bg-[#F5F5F5] rounded-r-lg transition-colors"
              >
                <ChevronDown className="w-5 h-5 text-[#FF6600] rotate-[-90deg]" />
              </button>
            </div>
            
            <span className="text-white/70 px-1">·</span>
            
            {searchParams?.return_date && (
              <div className="flex items-center bg-white rounded-lg shadow-sm">
                <button 
                  onClick={() => adjustDate('return', -1)}
                  className="p-2 hover:bg-[#F5F5F5] rounded-l-lg transition-colors"
                >
                  <ChevronUp className="w-5 h-5 text-[#FF6600] rotate-[-90deg]" />
                </button>
                <div className="px-3 py-1.5 min-w-[90px] text-center">
                  <span className="font-medium text-[#222222] text-sm">
                    {(() => {
                      const d = new Date(searchParams.return_date);
                      const weekday = d.toLocaleDateString('en-GB', { weekday: 'short' });
                      const day = d.getDate();
                      const month = d.toLocaleDateString('en-GB', { month: 'short' });
                      return `${weekday}, ${day} ${month}`;
                    })()}
                  </span>
                </div>
                <button 
                  onClick={() => adjustDate('return', 1)}
                  className="p-2 hover:bg-[#F5F5F5] rounded-r-lg transition-colors"
                >
                  <ChevronDown className="w-5 h-5 text-[#FF6600] rotate-[-90deg]" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* INLINE SEARCH PANEL - Skyscanner-style expandable search */}
      {showInlineSearch && (
        <div className="bg-white border-x border-slate-200 shadow-lg animate-in slide-in-from-top-2 duration-200">
          <div className="p-4">
            <FlightSearch 
              onSearch={(searchData) => {
                setShowInlineSearch(false);
                if (onInlineSearch) {
                  onInlineSearch(searchData);
                }
              }}
              initialData={{
                origin: searchParams?.origin,
                destination: searchParams?.destination,
                departure_date: searchParams?.departure_date,
                return_date: searchParams?.return_date,
                adults: searchParams?.adults || 1,
                youth: searchParams?.youth || 0,
                children: searchParams?.children || 0,
                infants: searchParams?.infants || 0,
                travel_class: searchParams?.cabin_class || searchParams?.travel_class || 'economy',
                direct_flights: searchParams?.direct_flights || false,
                trip_type: searchParams?.return_date ? 'round-trip' : 'one-way'
              }}
              isInline={true}
            />
          </div>
        </div>
      )}
      
      {/* FLEXIBLE DATES BAR - Hidden on mobile, visible on tablet+ */}
      <div className={`bg-white border border-[#E5E7EB] shadow-sm mb-4 hidden sm:block overflow-hidden ${showInlineSearch ? '' : 'rounded-b-xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#FF6600]">
          <div className="flex items-center gap-2 text-white">
            <CalendarDays className="w-4 h-4" />
            <span className="text-sm font-medium">Flexible Dates</span>
            <span className="text-xs text-white/80">Full Month</span>
          </div>
        </div>
        
        {/* Single Row Date Options - No gaps */}
        <div className="flex items-stretch">
          {/* Date Options - Scrollable Row */}
          <div className="flex-1 flex items-stretch overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {flexibleDateOptions.map((option, idx) => (
              <button
                key={idx}
                onClick={() => {
                  // If clicking a date that requires new search, trigger search with new date
                  if (option.isFromCalendar && onInlineSearch) {
                    // Trigger new search with the selected date
                    const tripDuration = searchParams?.return_date 
                      ? Math.round((new Date(searchParams.return_date) - new Date(searchParams.departure_date)) / (1000 * 60 * 60 * 24))
                      : null;
                    const newReturnDate = tripDuration 
                      ? new Date(new Date(option.departureDateStr).getTime() + tripDuration * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                      : null;
                    
                    onInlineSearch({
                      ...searchParams,
                      departure_date: option.departureDateStr,
                      return_date: newReturnDate
                    });
                  } else {
                    setSelectedDateOffset(option.offset);
                  }
                }}
                data-testid={`flexi-date-${option.offset}`}
                className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[70px] px-1 py-2 border-r border-[#E5E7EB] transition-all ${
                  option.isCurrentSearch
                    ? 'bg-[#FF6600] text-white'
                    : option.isSelected
                      ? 'bg-[#FF6600] text-white'
                      : option.price
                        ? 'bg-white text-[#222222] hover:bg-[#F5F5F5] cursor-pointer'
                        : 'bg-[#F5F5F5] text-slate-400 cursor-not-allowed'
                }`}
                disabled={!option.price}
              >
                <div className={`text-[9px] font-medium ${
                  option.isCurrentSearch ? 'text-white' : 
                  option.isWeekend ? 'text-[#FF6600]' : 'text-slate-500'
                }`}>
                  {option.dayLabel}
                </div>
                <div className={`text-[11px] font-medium ${
                  option.isCurrentSearch ? 'text-white' : 'text-[#222222]'
                }`}>
                  {option.dateLabel}
                </div>
                <div className={`text-xs font-bold mt-0.5 ${
                  option.isLowest && !option.isCurrentSearch ? 'text-green-600' : 
                  option.isCurrentSearch ? 'text-white' : 
                  option.price ? 'text-[#222222]' : 'text-slate-400'
                }`}>
                  {option.price ? <Money gbp={Math.round(option.price)} /> : '—'}
                </div>
                {option.isLowest && (
                  <div className={`text-[8px] font-semibold ${option.isCurrentSearch ? 'text-white' : 'text-green-600'}`}>Lowest</div>
                )}
                {option.isCurrentSearch && !option.isLowest && (
                  <div className="text-[8px] text-white/80 font-medium">Selected</div>
                )}
              </button>
            ))}
          </div>
          
          {/* All Dates Button - Fixed on Right */}
          <button 
            onClick={() => {
              // Initialize calendar with current search dates
              if (searchParams?.departure_date) {
                const depDate = new Date(searchParams.departure_date);
                setCalendarDepartDate(depDate);
                setCalendarMonth(depDate);
              }
              if (searchParams?.return_date) {
                setCalendarReturnDate(new Date(searchParams.return_date));
              }
              setShowFullCalendar(true);
            }}
            data-testid="flexi-date-all"
            className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[90px] px-3 py-3 border-l border-[#E5E7EB] transition-all ${
              selectedDateOffset === null
                ? 'bg-[#FF6600] text-white'
                : 'bg-white text-[#222222] hover:bg-[#F5F5F5]'
            }`}
          >
            <CalendarDays className={`w-5 h-5 mb-1 ${selectedDateOffset === null ? 'text-white' : 'text-[#FF6600]'}`} />
            <div className={`text-xs font-semibold ${selectedDateOffset === null ? 'text-white' : 'text-[#222222]'}`}>All Dates</div>
            <div className={`text-[10px] ${selectedDateOffset === null ? 'text-white/80' : 'text-slate-500'}`}>
              {flights.length} flights
            </div>
          </button>
        </div>
      </div>
      
      {/* Sorting tabs moved to Flights Available header */}

      {/* Mobile Filter Button - Shows at bottom on mobile */}
      <MobileFilterButton filterCount={activeFilterCount} onClearAll={clearAllFilters}>
        {renderFilterContent()}
      </MobileFilterButton>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* LEFT SIDEBAR - All Filters - FULL HEIGHT */}
        <div className="hidden lg:block w-72 flex-shrink-0 self-stretch">
          <Card className="h-full overflow-hidden border border-gray-200 shadow-sm rounded-xl bg-white">
            {/* Orange Header */}
            <div className="p-4 bg-[#FF6600]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-white" />
                  <span className="font-semibold text-white">{filteredFlights.length + (pinnedFlights?.length ? 1 : 0)} Results</span>
                </div>
                {(filters.stopsOutbound || filters.stopsReturn || filters.airlines.length > 0 || filters.baggage || filters.minPrice || filters.maxPrice || filters.outboundDepartureTime || filters.returnDepartureTime || filters.fareType) && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-white/90 hover:text-white hover:bg-white/15 text-xs h-7 px-2">
                    Clear all
                  </Button>
                )}
              </div>
            </div>
            
            {/* Scrollable Filter Content - FULL HEIGHT */}
            <div 
              className="p-4 space-y-4 overflow-y-auto flex-1"
              style={{ minHeight: '1500px' }}
            >
              {/* Fare Type Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Fare Type {uniqueFareTypes.length > 0 && `(${uniqueFareTypes.length})`}</label>
                {uniqueFareTypes.length > 0 ? (
                  <div className="space-y-1.5">
                    {uniqueFareTypes.map(fareType => (
                      <label key={fareType} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.fareType === fareType ? 'bg-blue-50' : ''}`}>
                        <input 
                          type="radio" 
                          name="fareType" 
                          checked={filters.fareType === fareType}
                          onChange={() => {
                            setFilters(prev => ({ ...prev, fareType: fareType }));
                            // Scroll to show filtered results at top
                            setTimeout(() => scrollToFlightResults(), 100);
                          }}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className={`text-sm ${filters.fareType === fareType ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{fareType}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No fare type data available</p>
                )}
              </div>

              <Separator />

              {/* Stops from Origin Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Stops from {searchParams?.origin || 'Origin'}</label>
                <div className="space-y-1.5">
                  {stopOptions.map(option => (
                    <label key={option.value} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.stopsOutbound === option.value ? 'bg-blue-50' : ''}`}>
                      <input 
                        type="radio" 
                        name="stopsOutbound" 
                        checked={filters.stopsOutbound === option.value}
                        onChange={() => setFilters(prev => ({ ...prev, stopsOutbound: option.value }))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className={`text-sm ${filters.stopsOutbound === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {isRoundTrip && (
                <>
                  <Separator />
                  
                  {/* Stops from Destination Filter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Stops from {searchParams?.destination || 'Destination'}</label>
                    <div className="space-y-1.5">
                      {stopOptions.map(option => (
                        <label key={option.value} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.stopsReturn === option.value ? 'bg-blue-50' : ''}`}>
                          <input 
                            type="radio" 
                            name="stopsReturn" 
                            checked={filters.stopsReturn === option.value}
                            onChange={() => setFilters(prev => ({ ...prev, stopsReturn: option.value }))}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className={`text-sm ${filters.stopsReturn === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Price Range Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Price Range</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="number" 
                    placeholder="Min" 
                    value={filters.minPrice || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value ? Number(e.target.value) : null }))}
                    className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                  <span className="text-gray-400">-</span>
                  <input 
                    type="number" 
                    placeholder="Max" 
                    value={filters.maxPrice || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value ? Number(e.target.value) : null }))}
                    className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <Separator />

              {/* Baggage Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Baggage</label>
                <div className="space-y-1.5">
                  {baggageOptions.map(option => (
                    <label key={option.value} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.baggage === option.value ? 'bg-blue-50' : ''}`}>
                      <input 
                        type="radio" 
                        name="baggage" 
                        checked={filters.baggage === option.value}
                        onChange={() => setFilters(prev => ({ ...prev, baggage: option.value }))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className={`text-sm ${filters.baggage === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Depart from Origin Time Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Depart from {searchParams?.origin || 'Origin'}</label>
                <div className="space-y-1.5">
                  {timeOptions.map(option => (
                    <label key={option.value} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.outboundDepartureTime === option.value ? 'bg-blue-50' : ''}`}>
                      <input 
                        type="radio" 
                        name="outboundDepartureTime" 
                        checked={filters.outboundDepartureTime === option.value}
                        onChange={() => setFilters(prev => ({ ...prev, outboundDepartureTime: option.value }))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className={`text-sm ${filters.outboundDepartureTime === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                        {option.label} <span className="text-gray-400 text-xs">({option.time})</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {isRoundTrip && (
                <>
                  <Separator />
                  
                  {/* Return Departure Time Filter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Return from {searchParams?.destination || 'Destination'}</label>
                    <div className="space-y-1.5">
                      {timeOptions.map(option => (
                        <label key={option.value} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.returnDepartureTime === option.value ? 'bg-blue-50' : ''}`}>
                          <input 
                            type="radio" 
                            name="returnDepartureTime" 
                            checked={filters.returnDepartureTime === option.value}
                            onChange={() => setFilters(prev => ({ ...prev, returnDepartureTime: option.value }))}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className={`text-sm ${filters.returnDepartureTime === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                            {option.label} <span className="text-gray-400 text-xs">({option.time})</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Airlines Filter - Show ALL */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Airlines ({availableAirlines.length})</label>
                <div className="space-y-1.5">
                  {availableAirlines.map(airline => (
                    <label key={airline} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.airlines.includes(airline) ? 'bg-blue-50' : ''}`}>
                      <input 
                        type="checkbox" 
                        checked={filters.airlines.includes(airline)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({ ...prev, airlines: [...prev.airlines, airline] }));
                            // Scroll to show filtered results at top
                            setTimeout(() => scrollToFlightResults(), 100);
                          } else {
                            setFilters(prev => ({ ...prev, airlines: prev.airlines.filter(a => a !== airline) }));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className={`text-sm ${filters.airlines.includes(airline) ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{airline}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Connection Length Outbound Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Connection Length ({searchParams?.origin} → {searchParams?.destination})</label>
                <div className="space-y-1.5">
                  {connectionLengthOptions.map(option => (
                    <label key={option.value} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.connectionLengthOutbound === option.value ? 'bg-blue-50' : ''}`}>
                      <input 
                        type="radio" 
                        name="connectionLengthOutbound" 
                        checked={filters.connectionLengthOutbound === option.value}
                        onChange={() => {
                          setFilters(prev => ({ ...prev, connectionLengthOutbound: option.value }));
                          // Scroll to show filtered results at top
                          setTimeout(() => scrollToFlightResults(), 100);
                        }}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className={`text-sm ${filters.connectionLengthOutbound === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                        {option.label} {option.time && <span className="text-gray-400 text-xs">({option.time})</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {isRoundTrip && (
                <>
                  <Separator />
                  
                  {/* Connection Length Return Filter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Connection Length ({searchParams?.destination} → {searchParams?.origin})</label>
                    <div className="space-y-1.5">
                      {connectionLengthOptions.map(option => (
                        <label key={option.value} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.connectionLengthReturn === option.value ? 'bg-blue-50' : ''}`}>
                          <input 
                            type="radio" 
                            name="connectionLengthReturn" 
                            checked={filters.connectionLengthReturn === option.value}
                            onChange={() => {
                              setFilters(prev => ({ ...prev, connectionLengthReturn: option.value }));
                              // Scroll to show filtered results at top
                              setTimeout(() => scrollToFlightResults(), 100);
                            }}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className={`text-sm ${filters.connectionLengthReturn === option.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                            {option.label} {option.time && <span className="text-gray-400 text-xs">({option.time})</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Departure Airport Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Departure Airport {uniqueDepartureAirports.length > 0 && `(${uniqueDepartureAirports.length})`}</label>
                {uniqueDepartureAirports.length > 0 ? (
                  <div className="space-y-1.5">
                    {uniqueDepartureAirports.map(airport => (
                      <label key={airport} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.departureAirport?.includes(airport) ? 'bg-blue-50' : ''}`}>
                        <input 
                          type="checkbox" 
                          checked={filters.departureAirport?.includes(airport) || false}
                          onChange={(e) => {
                            setFilters(prev => ({
                              ...prev,
                              departureAirport: e.target.checked 
                                ? [...(prev.departureAirport || []), airport]
                                : (prev.departureAirport || []).filter(a => a !== airport)
                            }));
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">{airport}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Single departure airport</p>
                )}
              </div>

              <Separator />

              {/* Connecting In Filter */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Connecting In {uniqueConnectingAirports.length > 0 && `(${uniqueConnectingAirports.length})`}</label>
                {uniqueConnectingAirports.length > 0 ? (
                  <div className="space-y-1.5">
                    {uniqueConnectingAirports.map(airport => (
                      <label key={airport} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded ${filters.connectingIn?.includes(airport) ? 'bg-blue-50' : ''}`}>
                        <input 
                          type="checkbox" 
                          checked={filters.connectingIn?.includes(airport) || false}
                          onChange={(e) => {
                            setFilters(prev => ({
                              ...prev,
                              connectingIn: e.target.checked 
                                ? [...(prev.connectingIn || []), airport]
                                : (prev.connectingIn || []).filter(a => a !== airport)
                            }));
                            // Scroll to show filtered results at top when selecting
                            if (e.target.checked) {
                              setTimeout(() => scrollToFlightResults(), 100);
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">{airport}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Direct flights only</p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* RIGHT SIDE - Flight Results */}
        <div className="flex-1 min-w-0">
        {/* Flights Available Header with Sorting and Price Toggle - All on one row */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 sm:p-4 rounded-xl mb-4 shadow-sm border border-gray-100">
          {/* Left: Flights Available Count */}
          <h2 className="text-base sm:text-xl font-bold text-[#222222] whitespace-nowrap">
            <span className="text-[#FF6600] font-bold">{filteredFlights.length + (pinnedFlights?.length ? 1 : 0)}</span> Flight{(filteredFlights.length + (pinnedFlights?.length ? 1 : 0)) !== 1 ? 's' : ''} Available
            {pinnedFlights && pinnedFlights.length > 0 && (
              <span className="text-xs font-normal text-[#FF6600] ml-1">
                (+ {pinnedAirlineName || 'Featured'} Direct)
              </span>
            )}
          </h2>
          
          {/* Center: Best / Cheapest / Fastest Sorting Buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => setSortBy('best')}
              className={`flex flex-col items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all min-w-[70px] sm:min-w-[90px] ${
                sortBy === 'best'
                  ? 'bg-[#FF6600] text-white'
                  : 'bg-[#F5F5F5] text-[#222222] hover:bg-[#E5E7EB]'
              }`}
            >
              <div className="flex items-center gap-0.5">
                {sortBy === 'best' && <Check className="h-3 w-3" />}
                <span>Best</span>
              </div>
              <div className={`text-[10px] sm:text-xs ${sortBy === 'best' ? 'text-white/90' : 'text-[#FF6600]'}`}>
                <Money gbp={sortingStats.best.price} />
              </div>
            </button>
            <button
              onClick={() => setSortBy('price')}
              className={`flex flex-col items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all min-w-[70px] sm:min-w-[90px] ${
                sortBy === 'price'
                  ? 'bg-[#FF6600] text-white'
                  : 'bg-[#F5F5F5] text-[#222222] hover:bg-[#E5E7EB]'
              }`}
            >
              <div className="flex items-center gap-0.5">
                {sortBy === 'price' && <Check className="h-3 w-3" />}
                <span>Cheapest</span>
              </div>
              <div className={`text-[10px] sm:text-xs ${sortBy === 'price' ? 'text-white/90' : 'text-[#FF6600]'}`}>
                <Money gbp={sortingStats.cheapest.price} />
              </div>
            </button>
            <button
              onClick={() => setSortBy('duration')}
              className={`flex flex-col items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all min-w-[70px] sm:min-w-[90px] ${
                sortBy === 'duration'
                  ? 'bg-[#FF6600] text-white'
                  : 'bg-[#F5F5F5] text-[#222222] hover:bg-[#E5E7EB]'
              }`}
            >
              <div className="flex items-center gap-0.5">
                {sortBy === 'duration' && <Check className="h-3 w-3" />}
                <span>Fastest</span>
              </div>
              <div className={`text-[10px] sm:text-xs ${sortBy === 'duration' ? 'text-white/90' : 'text-[#FF6600]'}`}>
                <Money gbp={sortingStats.fastest.price} />
              </div>
            </button>
          </div>
          
          {/* Right: Per Person / Total Price Toggle */}
          <div className="flex items-center bg-[#F5F5F5] rounded-full p-0.5 border border-[#E5E7EB]" data-testid="price-toggle-main">
            <button
              onClick={() => setPriceDisplayMode('perPerson')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                priceDisplayMode === 'perPerson'
                  ? 'bg-[#FF6600] text-white shadow-md'
                  : 'text-[#222222] hover:text-[#FF6600]'
              }`}
            >
              Per Person
            </button>
            <button
              onClick={() => setPriceDisplayMode('total')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                priceDisplayMode === 'total'
                  ? 'bg-[#FF6600] text-white shadow-md'
                  : 'text-[#222222] hover:text-[#FF6600]'
              }`}
            >
              Total Price
            </button>
          </div>
        </div>
        
        {/* Revalidation Error Banner */}
        {revalidationError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="revalidation-error">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-red-800 font-semibold">Flight No Longer Available</h3>
                <p className="text-red-700 text-sm mt-1">{revalidationError.message}</p>
                {revalidationError.alternatives && revalidationError.alternatives.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-red-700 font-medium">Try these alternatives:</p>
                    <ul className="mt-2 space-y-1">
                      {revalidationError.alternatives.map((alt, idx) => (
                        <li key={idx} className="text-sm text-red-600">
                          • {alt.airline} {alt.flight_number} at {alt.departure_time} - <Money gbp={Math.round(alt.price)} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button 
                onClick={() => onClearRevalidationError && onClearRevalidationError()}
                className="flex-shrink-0 text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {revalidationError.shouldRefresh && (
              <Button 
                onClick={onModifySearch}
                className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                size="sm"
              >
                Refresh Search Results
              </Button>
            )}
          </div>
        )}
          
          {/* Combined View - Shows full round-trip packages */}
          {(!isRoundTrip || viewMode === 'combined') && (
            <div className="space-y-3 sm:space-y-4 pb-24 lg:pb-4">
              
              {/* ========================================== */}
              {/* PINNED AIRLINE DIRECT FLIGHTS SECTION */}
              {/* ========================================== */}
              {pinnedFlights && pinnedFlights.length > 0 && (
                <div className="mb-4 sm:mb-6" data-testid="pinned-flights-section">
                  {/* Dynamic Header based on airline */}
                  <div className={`${pinnedColors.header} rounded-t-xl px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 sm:w-5 sm:h-5 text-white fill-white" />
                      <span className="text-white font-bold text-sm sm:text-base">
                        {pinnedAirlineName || 'Recommended'} Direct Flights
                      </span>
                    </div>
                    <span className="bg-white/20 text-white text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-full">
                      Best Price
                    </span>
                  </div>
                  
                  {/* Show only the FIRST (best priced) pinned flight */}
                  <div className={`border-2 ${pinnedColors.border} border-t-0 rounded-b-xl bg-gray-50 p-2 sm:p-4`}>
                    {pinnedFlights.slice(0, 1).map((flight, index) => {
                      const { price, caption } = cardPrice(flight);
                      return (
                        <BfmFlightCard
                          key={`pinned-${flight.id || index}`}
                          leg={toLeg(flight)}
                          returnLeg={isRoundTrip ? toReturnLeg(flight) : null}
                          itin={flight}
                          price={price}
                          priceCaption={caption}
                          badges={[{ text: 'Best price', tone: 'orange' }]}
                          highlight
                          onSelect={() => onSelectFlight(flight)}
                          disabled={isRevalidating}
                          selectLabel={isRevalidating ? 'Checking...' : 'Select'}
                          testId={`pinned-flight-card-${index}`}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Separator */}
                  <div className="flex items-center gap-3 mt-4 sm:mt-6 mb-3 sm:mb-4">
                    <div className="flex-1 h-px bg-slate-200"></div>
                    <span className="text-[10px] sm:text-xs text-slate-400">More flights</span>
                    <div className="flex-1 h-px bg-slate-200"></div>
                  </div>
                </div>
              )}
              
              {/* Pagination Info - Mobile optimized */}
              {filteredFlights.length > flightsPerPage && (
                <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-lg p-2 sm:p-3 shadow-sm border border-slate-100 gap-1 sm:gap-0">
                  <div className="text-xs sm:text-sm text-slate-600">
                    <span className="font-semibold">{((currentPage - 1) * flightsPerPage) + 1}</span>-<span className="font-semibold">{Math.min(currentPage * flightsPerPage, filteredFlights.length)}</span> of <span className="font-semibold">{filteredFlights.length}</span> flights
                  </div>
                  <div className="text-xs sm:text-sm text-slate-500">
                    Page {currentPage}/{Math.ceil(filteredFlights.length / flightsPerPage)}
                  </div>
                </div>
              )}
              
              {/* Flight Results List - with data-testid for scroll targeting */}
              <div data-testid="flight-results-list">
              {/* Local-time clarifier (hover over any time to see the airport) */}
              <div
                className="flex items-center gap-2 mb-3 text-[11px] sm:text-xs text-slate-500"
                data-testid="local-time-note"
              >
                <Info className="h-3 w-3" />
                <span>All times shown are local airport time — hover any time to see which airport.</span>
              </div>
              {filteredFlights.slice((currentPage - 1) * flightsPerPage, currentPage * flightsPerPage).map((flight, index) => {
                const { price, caption } = cardPrice(flight);
                const badges = [
                  flight.price === cheapestPrice && { text: 'Best price', tone: 'green' },
                  flight.low_cost && { text: 'Low-cost', tone: 'soft', testId: `low-cost-badge-${flight.id}` },
                  flight.is_different_airlines && { text: 'Combined fare', tone: 'dark' },
                  flight.is_mix_match && !flight.is_different_airlines && { text: 'Combined fare', tone: 'outline' },
                ];
                const alternatives = Array.isArray(flight.alternatives) && flight.alternatives.length > 0 ? (
                  <div className="pt-3 border-t border-dashed border-slate-200 flex flex-wrap items-center gap-2 text-xs" data-testid={`alternatives-${index}`}>
                    <span className="text-slate-500 font-medium">Same flights also available via:</span>
                    {flight.alternatives.map((alt, ai) => (
                      <button
                        key={alt.id || ai}
                        type="button"
                        disabled={isRevalidating}
                        onClick={() => onSelectFlight(alt)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 font-semibold text-slate-700 hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
                        data-testid={`alternative-${index}-${ai}`}
                      >
                        {alt.low_cost ? <span className="text-[9px] uppercase bg-[#FF6600] text-white rounded-full px-1.5 py-0.5">Low-cost</span> : <span className="text-[9px] uppercase bg-slate-800 text-white rounded-full px-1.5 py-0.5">{alt.source === 'sabre' ? 'GDS' : 'Airline direct'}</span>}
                        {alt.fare_brand ? `${alt.fare_brand} · ` : ''}<Money gbp={Number(alt.price || 0).toFixed(2)} />
                        {alt.baggage_included ? <span className="text-emerald-600">· bag incl.</span> : null}
                      </button>
                    ))}
                  </div>
                ) : null;
                return (
                  <div key={flight.id || index} className="mb-3 sm:mb-4" data-testid={`flight-card-${index}`}>
                    <BfmFlightCard
                      leg={toLeg(flight)}
                      returnLeg={isRoundTrip ? toReturnLeg(flight) : null}
                      itin={flight}
                      price={price}
                      priceCaption={caption}
                      badges={badges}
                      title={flight.is_different_airlines ? (flight.mix_match_label || `${flight.airline_code} + ${flight.return_airline_code_display || flight.return_airline_code}`) : undefined}
                      secondaryAirlineCode={flight.is_different_airlines ? (flight.return_airline_code_display || flight.return_airline_code) : undefined}
                      onSelect={() => onSelectFlight(flight)}
                      disabled={isRevalidating}
                      selectLabel={isRevalidating ? 'Checking...' : 'Select'}
                      testId={`select-flight-${index}`}
                      footer={alternatives}
                    />
                  </div>
                );
              })}
              </div>
              
              {/* Pagination Controls */}
              {filteredFlights.length > flightsPerPage && (
                <div className="flex items-center justify-center gap-2 py-6 bg-white rounded-lg shadow-sm border border-slate-100 mt-4">
                  {/* Previous Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); scrollToTop(); }}
                    disabled={currentPage === 1}
                    className="px-3"
                  >
                    <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                    Prev
                  </Button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const totalPages = Math.ceil(filteredFlights.length / flightsPerPage);
                      const pages = [];
                      
                      // Always show first page
                      pages.push(1);
                      
                      // Show ellipsis if needed
                      if (currentPage > 3) {
                        pages.push('...');
                      }
                      
                      // Show pages around current
                      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                        if (!pages.includes(i)) {
                          pages.push(i);
                        }
                      }
                      
                      // Show ellipsis if needed
                      if (currentPage < totalPages - 2) {
                        pages.push('...');
                      }
                      
                      // Always show last page
                      if (totalPages > 1 && !pages.includes(totalPages)) {
                        pages.push(totalPages);
                      }
                      
                      return pages.map((page, idx) => (
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
                        ) : (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => { setCurrentPage(page); scrollToTop(); }}
                            className={`min-w-[40px] ${currentPage === page ? 'bg-brand-600 hover:bg-brand-700' : ''}`}
                          >
                            {page}
                          </Button>
                        )
                      ));
                    })()}
                  </div>
                  
                  {/* Next Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setCurrentPage(p => Math.min(Math.ceil(filteredFlights.length / flightsPerPage), p + 1)); scrollToTop(); }}
                    disabled={currentPage === Math.ceil(filteredFlights.length / flightsPerPage)}
                    className="px-3"
                  >
                    Next
                    <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Mix & Match Separate View removed per UX recommendation - Combined fares only */}

          {filteredFlights.length === 0 && (
            <Card className="p-12 text-center">
              {flights.length === 0 ? (
                // No flights returned from search at all
                <>
                  <Plane className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-xl font-semibold text-slate-700">No Flights Found</p>
                  <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                    We couldn't find any flights for your selected route and dates. 
                    {searchParams?.direct_flights && " This may be because you selected 'Direct flights only' - try including connecting flights."}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <Button 
                      onClick={() => setShowInlineSearch(true)}
                      className="bg-brand-600 hover:bg-brand-700 text-white"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Modify Search
                    </Button>
                    {searchParams?.direct_flights && (
                      <Button 
                        variant="outline"
                        onClick={() => {
                          // Trigger a new search without direct flights filter
                          if (onInlineSearch) {
                            onInlineSearch({
                              ...searchParams,
                              direct_flights: false,
                              directFlights: false
                            });
                          }
                        }}
                        className="border-brand-600 text-brand-600 hover:bg-brand-50"
                      >
                        Include Connecting Flights
                      </Button>
                    )}
                  </div>
                </>
              ) : filters.stopsOutbound === 'direct' || filters.stopsReturn === 'direct' ? (
                <>
                  <Plane className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-lg font-medium text-slate-700">No Direct Flights Available</p>
                  <p className="text-sm text-slate-500 mt-2">There are no nonstop flights available for this route. Try allowing connections or searching different dates.</p>
                  <Button variant="outline" onClick={clearAllFilters} className="mt-4 text-brand-600 border-brand-600 hover:bg-brand-50">
                    Clear All Filters
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-lg text-slate-600">No flights match your filters. Try adjusting your search criteria.</p>
                  <Button variant="outline" onClick={clearAllFilters} className="mt-4 text-brand-600 border-brand-600 hover:bg-brand-50">
                    Clear All Filters
                  </Button>
                </>
              )}
            </Card>
          )}
        </div>
      </div>
      
      {/* Full Calendar Modal - Skyscanner Style */}
      {showFullCalendar && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-white">
                  <Plane className="w-5 h-5" />
                  <span className="font-semibold">{searchParams?.origin || searchParams?.from || 'LON'}</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-semibold">{searchParams?.destination || searchParams?.to || 'DXB'}</span>
                </div>
                <Badge className="bg-white/20 text-white">
                  {isRoundTrip ? 'Round Trip' : 'One Way'}
                </Badge>
              </div>
              <button 
                onClick={() => setShowFullCalendar(false)}
                className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Selected Dates Display */}
            <div className="px-6 py-3 bg-slate-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Depart:</span>
                  <span className="font-semibold text-slate-800">
                    {calendarDepartDate ? format(calendarDepartDate, 'EEE, MMM d, yyyy') : 'Select date'}
                  </span>
                </div>
                {isRoundTrip && (
                  <>
                    <div className="w-px h-6 bg-slate-300" />
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Return:</span>
                      <span className="font-semibold text-slate-800">
                        {calendarReturnDate ? format(calendarReturnDate, 'EEE, MMM d, yyyy') : 'Select date'}
                      </span>
                    </div>
                  </>
                )}
              </div>
              {calendarDepartDate && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setCalendarDepartDate(null); setCalendarReturnDate(null); }}
                  className="text-slate-500"
                >
                  Clear
                </Button>
              )}
            </div>
            
            {/* Calendar Navigation */}
            <div className="px-6 py-3 flex items-center justify-between border-b">
              <button 
                data-testid="calendar-nav-prev"
                onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                disabled={calendarMonth <= new Date()}
                className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronDown className="w-5 h-5 rotate-90" />
              </button>
              <h3 data-testid="calendar-month-title" className="text-xl font-bold text-slate-800">
                {format(calendarMonth, 'MMMM yyyy')}
              </h3>
              <button 
                data-testid="calendar-nav-next"
                onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                disabled={calendarMonth >= new Date(new Date().getFullYear() + 1, new Date().getMonth())}
                className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronDown className="w-5 h-5 -rotate-90" />
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: '50vh' }}>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-slate-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const year = calendarMonth.getFullYear();
                  const month = calendarMonth.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const startPadding = firstDay.getDay();
                  const days = [];
                  
                  // Add padding for days before the 1st
                  for (let i = 0; i < startPadding; i++) {
                    days.push(<div key={`pad-${i}`} className="h-16" />);
                  }
                  
                  // Add days of the month
                  for (let day = 1; day <= lastDay.getDate(); day++) {
                    const date = new Date(year, month, day);
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const isPast = date < today;
                    const isDepart = calendarDepartDate && format(calendarDepartDate, 'yyyy-MM-dd') === dateStr;
                    const isReturn = calendarReturnDate && format(calendarReturnDate, 'yyyy-MM-dd') === dateStr;
                    const isBetween = calendarDepartDate && calendarReturnDate && 
                      date > calendarDepartDate && date < calendarReturnDate;
                    
                    // Get estimated price for this date
                    const estimatedPrice = fareCalendarPrices[dateStr];
                    const hasPrice = estimatedPrice && estimatedPrice > 0;
                    
                    days.push(
                      <button
                        key={day}
                        disabled={isPast}
                        onClick={() => {
                          if (isPast) return;
                          if (!calendarDepartDate || (calendarDepartDate && calendarReturnDate)) {
                            // Start new selection
                            setCalendarDepartDate(date);
                            setCalendarReturnDate(null);
                          } else if (date > calendarDepartDate) {
                            // Set return date
                            setCalendarReturnDate(date);
                          } else {
                            // Reset and set new depart date
                            setCalendarDepartDate(date);
                            setCalendarReturnDate(null);
                          }
                        }}
                        className={`h-16 rounded-lg flex flex-col items-center justify-center transition-all ${
                          isPast 
                            ? 'opacity-30 cursor-not-allowed' 
                            : isDepart || isReturn
                              ? 'bg-brand-600 text-white shadow-lg scale-105'
                              : isBetween
                                ? 'bg-brand-100 text-brand-700'
                                : 'hover:bg-slate-100'
                        }`}
                      >
                        <span className={`text-sm font-semibold ${isDepart || isReturn ? 'text-white' : ''}`}>
                          {day}
                        </span>
                        {hasPrice && !isPast && (
                          <span className={`text-xs ${
                            isDepart || isReturn ? 'text-white/80' : 'text-green-600 font-medium'
                          }`}>
                            <Money gbp={Math.round(estimatedPrice)} />
                          </span>
                        )}
                        {isDepart && (
                          <span className="text-[10px] text-white/70">Depart</span>
                        )}
                        {isReturn && (
                          <span className="text-[10px] text-white/70">Return</span>
                        )}
                      </button>
                    );
                  }
                  
                  return days;
                })()}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
              <div className="text-sm text-slate-500">
                {calendarDepartDate && calendarReturnDate ? (
                  <span className="text-green-600 font-medium">
                    ✓ Ready to search: {format(calendarDepartDate, 'MMM d')} - {format(calendarReturnDate, 'MMM d, yyyy')}
                  </span>
                ) : calendarDepartDate ? (
                  <span className="text-amber-600">
                    Select return date to continue
                  </span>
                ) : (
                  <>
                    <CalendarDays className="w-4 h-4 inline mr-1" />
                    Select departure date first
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowFullCalendar(false);
                    setCalendarDepartDate(null);
                    setCalendarReturnDate(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-brand-600 hover:bg-brand-700 text-white"
                  disabled={!calendarDepartDate || (isRoundTrip && !calendarReturnDate)}
                  onClick={() => {
                    // Trigger new search with selected dates
                    const newSearchData = {
                      ...searchParams,
                      origin: searchParams?.origin || searchParams?.from,
                      destination: searchParams?.destination || searchParams?.to,
                      departure_date: format(calendarDepartDate, 'yyyy-MM-dd'),
                      return_date: calendarReturnDate ? format(calendarReturnDate, 'yyyy-MM-dd') : null
                    };
                    console.log('Calendar Search triggered:', newSearchData);
                    if (onInlineSearch) {
                      onInlineSearch(newSearchData);
                    }
                    setShowFullCalendar(false);
                  }}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Flights
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Flexible Dates Matrix Component
const FlexibleDatesMatrix = ({ flights, searchParams, onSelectFlight, onModifySearch, onSearchDates, isRevalidating, priceDisplayMode = 'perPerson' }) => {
  const [selectedDates, setSelectedDates] = useState(null); // { depDate, retDate }
  
  // Helper function to safely extract date from various formats
  const extractDate = (flight, dateField) => {
    try {
      // Try departure_date/return_departure_date first (YYYY-MM-DD format)
      if (dateField === 'departure' && flight.departure_date) {
        return flight.departure_date;
      }
      if (dateField === 'return' && flight.return_departure_date) {
        return flight.return_departure_date;
      }
      
      // Try full datetime fields
      const datetimeField = dateField === 'departure' ? 'departure_datetime' : 'return_departure_datetime';
      if (flight[datetimeField]) {
        const date = new Date(flight[datetimeField]);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
      
      // Fallback to departure_time/return_departure_time if it looks like ISO format
      const timeField = dateField === 'departure' ? 'departure_time' : 'return_departure_time';
      if (flight[timeField] && flight[timeField].includes('T')) {
        const date = new Date(flight[timeField]);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      }
      
      return '';
    } catch (e) {
      return '';
    }
  };
  
  const priceMatrix = useMemo(() => {
    const matrix = {};
    
    flights.forEach(flight => {
      const depDate = extractDate(flight, 'departure');
      const retDate = extractDate(flight, 'return');
      
      if (!depDate) return; // Skip flights without valid departure date
      
      const key = `${depDate}_${retDate}`;
      
      if (!matrix[key]) {
        matrix[key] = {
          cheapestFlight: flight,
          departureDate: depDate,
          returnDate: retDate,
          allFlights: [flight]
        };
      } else {
        matrix[key].allFlights.push(flight);
        if (flight.price < matrix[key].cheapestFlight.price) {
          matrix[key].cheapestFlight = flight;
        }
      }
    });
    
    return matrix;
  }, [flights]);

  const departureDates = useMemo(() => {
    const dates = [...new Set(Object.values(priceMatrix).map(f => f.departureDate))].filter(Boolean);
    return dates.sort();
  }, [priceMatrix]);

  const returnDates = useMemo(() => {
    const dates = [...new Set(Object.values(priceMatrix).map(f => f.returnDate))].filter(Boolean);
    return dates.sort();
  }, [priceMatrix]);

  const getPrice = (depDate, retDate) => {
    const key = `${depDate}_${retDate}`;
    return priceMatrix[key];
  };

  const formatDateHeader = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return format(date, 'EEE\nMMM dd');
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return format(date, 'EEEE, MMM dd, yyyy');
  };

  const getLowestPrice = () => {
    const prices = Object.values(priceMatrix).map(f => f.cheapestFlight.price);
    return Math.min(...prices);
  };

  const lowestPrice = getLowestPrice();

  // Get flights for selected date combination
  const selectedFlights = useMemo(() => {
    if (!selectedDates) return [];
    const key = `${selectedDates.depDate}_${selectedDates.retDate}`;
    return priceMatrix[key]?.allFlights || [];
  }, [selectedDates, priceMatrix]);

  const handleCellClick = (depDate, retDate, flight) => {
    if (!flight) {
      // Not fetched yet (we only pre-load 7 date pairs to keep GDS costs sane):
      // run an exact-date BFM search for this combination on demand.
      if (onSearchDates && depDate) {
        onSearchDates({ ...searchParams, departure_date: depDate, return_date: retDate || null, flexiDates: false });
      }
      return;
    }
    setSelectedDates({ depDate, retDate });
  };

  const formatTime = (dateTimeOrTime) => {
    if (!dateTimeOrTime) return '';
    // If it's already in HH:MM format, return as-is
    if (/^\d{2}:\d{2}$/.test(dateTimeOrTime)) {
      return dateTimeOrTime;
    }
    // Handle Sabre time format: "13:55:00Z" or "04:45:00+04:00"
    const timeMatch = dateTimeOrTime.match(/^(\d{2}):(\d{2})/);
    if (timeMatch) {
      return `${timeMatch[1]}:${timeMatch[2]}`;
    }
    try {
      const date = new Date(dateTimeOrTime);
      if (!isNaN(date.getTime())) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      }
    } catch (e) { /* ignore parsing errors */ }
    return dateTimeOrTime;
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration.replace('PT', '').replace('H', 'h ').replace('M', 'm');
  };

  // Count total unique date combinations (cells in matrix)
  const totalCombinations = Object.keys(priceMatrix).length;

  // One-way searches have no return dates: render the departure dates as the
  // columns of a single row instead of an empty grid.
  const isOneWayGrid = returnDates.length === 0;
  const columnDates = isOneWayGrid ? departureDates : returnDates;
  const rowDates = isOneWayGrid ? [''] : departureDates;
  const cellFor = (rowDate, colDate) => (isOneWayGrid ? getPrice(colDate, '') : getPrice(rowDate, colDate));
  const cellDates = (rowDate, colDate) => (isOneWayGrid ? { depDate: colDate, retDate: '' } : { depDate: rowDate, retDate: colDate });

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Search Summary Bar */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border p-4" data-testid="flex-grid-summary-bar">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Plane className="h-5 w-5 text-brand-600" />
              <span className="font-semibold">{searchParams?.origin}</span>
              <span className="text-slate-400">→</span>
              <span className="font-semibold">{searchParams?.destination}</span>
            </div>
            <span className="text-sm text-slate-600">
              {searchParams?.departure_date && format(new Date(searchParams.departure_date), 'EEE d MMM')}
              {searchParams?.return_date && ` – ${format(new Date(searchParams.return_date), 'EEE d MMM')}`}
              {' · '}{(searchParams?.adults || 1) + (searchParams?.youth || 0) + (searchParams?.children || 0) + (searchParams?.infants || 0)} passenger{((searchParams?.adults || 1) + (searchParams?.youth || 0) + (searchParams?.children || 0) + (searchParams?.infants || 0)) !== 1 ? 's' : ''}
            </span>
            <Badge className="bg-brand-100 text-brand-700">±3 Days Flexible</Badge>
          </div>
          <Button
            onClick={onModifySearch}
            className="bg-brand-600 hover:bg-brand-700 text-white font-semibold self-start sm:self-auto"
            data-testid="flex-grid-modify-search-btn"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Modify Search
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Flexible Dates Price Grid</h2>
          <p className="text-slate-600">Click on a date combination to see all available flights</p>
        </div>
        <Badge className="bg-brand-100 text-brand-700 px-4 py-2">
          {totalCombinations} date combinations • {flights.length} total flights
        </Badge>
      </div>

      <div className="overflow-x-auto">
        <Card className="inline-block min-w-full">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border p-4 bg-brand-50 font-semibold sticky left-0 z-10">
                  {isOneWayGrid ? (
                    <div className="text-sm text-brand-700">Depart →</div>
                  ) : (
                    <>
                      <div className="text-sm text-brand-700">Depart ↓</div>
                      <div className="text-sm text-brand-700">Return →</div>
                    </>
                  )}
                </th>
                {columnDates.map(colDate => (
                  <th key={colDate} className="border p-2 bg-brand-50 min-w-[100px]" data-testid={`flex-grid-col-${colDate}`}>
                    <div className="text-sm whitespace-pre-line text-center text-brand-700">{formatDateHeader(colDate)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowDates.map(rowDate => (
                <tr key={rowDate || 'one-way'}>
                  <td className="border p-2 bg-brand-50 font-medium sticky left-0 z-10">
                    <div className="text-sm whitespace-pre-line text-brand-700">{isOneWayGrid ? 'One Way' : formatDateHeader(rowDate)}</div>
                  </td>
                  {columnDates.map(colDate => {
                    const { depDate, retDate } = cellDates(rowDate, colDate);
                    const cellData = cellFor(rowDate, colDate);
                    const flight = cellData?.cheapestFlight;
                    const isLowest = flight && flight.price === lowestPrice;
                    const isSelected = selectedDates?.depDate === depDate && selectedDates?.retDate === retDate;
                    
                    return (
                      <td
                        data-testid={`flex-grid-cell-${depDate}${retDate ? '_' + retDate : ''}`}
                        key={`${depDate}_${retDate}`}
                        className={`border p-2 text-center cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-brand-600 text-white ring-2 ring-brand-600 ring-offset-1' 
                            : isLowest 
                              ? 'bg-green-50 hover:bg-green-100' 
                              : 'hover:bg-brand-50'
                        }`}
                        onClick={() => handleCellClick(depDate, retDate, cellData)}
                      >
                        {cellData ? (
                          <div>
                            {(() => {
                              const totalPassengers = (searchParams?.adults || 1) + 
                                (searchParams?.youth || 0) +
                                (searchParams?.children || 0) + 
                                (searchParams?.infants || 0);
                              // API returns TOTAL price, calculate per person
                              const totalPrice = Math.round(flight.price);
                              const pricePerPerson = Math.round(flight.price / totalPassengers);
                              
                              if (priceDisplayMode === 'total') {
                                return (
                                  <>
                                    <div className={`text-sm font-bold ${
                                      isSelected 
                                        ? 'text-white' 
                                        : isLowest 
                                          ? 'text-green-700' 
                                          : 'text-slate-700'
                                    }`}>
                                      <Money gbp={totalPrice.toLocaleString()} />
                                    </div>
                                    <div className={`text-[10px] ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                                      total
                                    </div>
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    <div className={`text-sm font-bold ${
                                      isSelected 
                                        ? 'text-white' 
                                        : isLowest 
                                          ? 'text-green-700' 
                                          : 'text-slate-700'
                                    }`}>
                                      <Money gbp={pricePerPerson.toLocaleString()} />
                                    </div>
                                    <div className={`text-[10px] ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                                      pp
                                    </div>
                                  </>
                                );
                              }
                            })()}
                            <div className={`text-xs ${isSelected ? 'text-white/80' : 'text-slate-500'}`}>
                              {flight.airline_code}
                            </div>
                            {cellData.allFlights.length > 1 && (
                              <div className={`text-xs ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                                +{cellData.allFlights.length - 1} more
                              </div>
                            )}
                            {isLowest && !isSelected && (
                              <div className="text-xs text-green-700 font-semibold mt-1">Lowest</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-brand-600 font-medium hover:underline" title="Search this date combination">
                            {(!isOneWayGrid && retDate && retDate <= depDate) ? <span className="text-slate-300">—</span> : 'Search'}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="mt-4 text-sm text-slate-600">
        <p>Click a price to see every flight for that date pair • Click <span className="text-brand-600 font-medium">Search</span> on any other cell to load live fares for that combination</p>
      </div>

      {/* Selected Date Flights */}
      {selectedDates && selectedFlights.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-slate-900">
                Flights for {formatDateLong(selectedDates.depDate)}{selectedDates.retDate ? ` → ${formatDateLong(selectedDates.retDate)}` : ' (one way)'}
              </h3>
              <p className="text-slate-600">{selectedFlights.length} flight(s) available</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setSelectedDates(null)}
              className="text-slate-600"
            >
              Clear Selection
            </Button>
          </div>
          
          <div className="space-y-4">
            {selectedFlights
              .sort((a, b) => a.price - b.price)
              .map((flight, index) => (
              <Card 
                key={index} 
                className="hover:shadow-lg transition-shadow cursor-pointer border-slate-200 hover:border-brand-300"
                onClick={() => onSelectFlight(flight)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    {/* Airline Info */}
                    <div className="flex items-center gap-4 min-w-[150px]">
                      <AirlineLogo code={flight.airline_code} prominent />
                      <div>
                        <div className="font-semibold text-slate-900">{flight.airline}</div>
                        <div className="text-sm text-slate-500">{flight.airline_code}</div>
                      </div>
                    </div>

                    {/* Outbound Flight */}
                    <div className="flex-1 px-6">
                      <div className="flex items-center justify-between">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-900">{formatTime(flight.departure_time)}</div>
                          <div className="text-sm font-medium text-brand-600">{flight.from}</div>
                        </div>
                        <div className="flex-1 px-4">
                          <div className="text-center text-xs text-slate-500 mb-1">{formatDuration(flight.duration)}</div>
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                            <div className="flex-1 h-px bg-slate-300 mx-2"></div>
                            <Plane className="h-4 w-4 text-brand-500 transform rotate-90" />
                            <div className="flex-1 h-px bg-slate-300 mx-2"></div>
                            <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                          </div>
                          <div className="text-center text-xs font-medium text-brand-600 mt-1">
                            {flight.is_direct ? 'Direct' : `${flight.stops} stop(s)`}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-900">{formatTime(flight.arrival_time)}</div>
                          <div className="text-sm font-medium text-brand-600">{flight.to}</div>
                        </div>
                      </div>
                    </div>

                    {/* Return Flight (if round-trip) */}
                    {flight.return_departure_time && (
                      <div className="flex-1 px-6 border-l">
                        <div className="flex items-center justify-between">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-slate-900">{formatTime(flight.return_departure_time)}</div>
                            <div className="text-sm font-medium text-brand-600">{flight.to}</div>
                          </div>
                          <div className="flex-1 px-4">
                            <div className="text-center text-xs text-slate-500 mb-1">{formatDuration(flight.return_duration)}</div>
                            <div className="flex items-center">
                              <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                              <div className="flex-1 h-px bg-slate-300 mx-2"></div>
                              <Plane className="h-4 w-4 text-brand-500 transform -rotate-90" />
                              <div className="flex-1 h-px bg-slate-300 mx-2"></div>
                              <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                            </div>
                            <div className="text-center text-xs font-medium text-brand-600 mt-1">
                              {flight.return_is_direct ? 'Direct' : `${flight.return_stops} stop(s)`}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-slate-900">{formatTime(flight.return_arrival_time)}</div>
                            <div className="text-sm font-medium text-brand-600">{flight.from}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Price & Book */}
                    <div className="text-right min-w-[140px] pl-6">
                      {(() => {
                        const totalPassengers = (searchParams?.adults || 1) + 
                          (searchParams?.youth || 0) +
                          (searchParams?.children || 0) + 
                          (searchParams?.infants || 0);
                        // API returns TOTAL price, calculate per person
                        const totalPrice = Math.round(flight.price);
                        const pricePerPerson = Math.round(flight.price / totalPassengers);
                        
                        if (priceDisplayMode === 'total') {
                          return (
                            <>
                              <div className="text-3xl font-bold text-brand-600"><Money gbp={totalPrice.toLocaleString()} /></div>
                              <div className="text-sm text-slate-500">total</div>
                            </>
                          );
                        } else {
                          return (
                            <>
                              <div className="text-3xl font-bold text-brand-600"><Money gbp={pricePerPerson.toLocaleString()} /></div>
                              <div className="text-sm text-slate-500">per person</div>
                            </>
                          );
                        }
                      })()}
                      <Button className="bg-[#FF6600] hover:bg-[#E65C00] active:bg-[#CC5200] focus-visible:ring-2 focus-visible:ring-[#FF6600] focus-visible:ring-offset-2 text-white w-full mt-2">
                        Select
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* No selection prompt */}
      {!selectedDates && (
        <div className="mt-8 text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
          <Plane className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">Select dates from the grid above</h3>
          <p className="text-slate-500">Click on any price cell to view all available flights for that date combination</p>
        </div>
      )}
    </div>
  );
};
